import React, { useEffect, useState } from 'react';

import { isRecord, unwrapList, unwrapRecord } from './api-client';
import type { AdminApiClient, JsonRecord } from './api-client';
import { pathFor } from './page-registry';
import './operations-workspace.css';
import { BusinessSecurityWorkspace } from './business-security-workspace';

type Params = Record<string, string>;
type LoadState<T = unknown> = { data?: T; error?: unknown; loading: boolean; reload: () => void };

interface PageProps {
  api: AdminApiClient;
  targetAppId: string;
  pageId: string;
  params: Params;
}

const labels: Record<string, string> = {
  ACCEPTED: '用户同意',
  ACTIVE: '启用',
  APPROVED: '已复核',
  CHANNEL_SETTLEMENT: '渠道结算',
  COMPLETED: '已完成',
  CSV: 'CSV 文件',
  DIFFERENCES: '有差异',
  DISABLED: '停用',
  FAILED: '失败',
  LONG_TERM: '长期订阅',
  MANUALLY_CONFIRMED: '人工确认',
  MATCHED: '一致',
  MEMBER_SUMMARY: '会员汇总',
  ONE_TIME: '一次订阅',
  ORDER_DETAIL: '订单明细',
  ORDER_PROFIT: '订单利润',
  PAYMENT_RECONCILIATION: '支付对账',
  PENDING: '待处理',
  PENDING_REVIEW: '凭证待复核',
  PROCESSING: '处理中',
  READY: '可下载',
  REJECTED: '用户拒绝',
  REVIEWED: '已核验',
  REVIEW_PENDING: '待复核',
  SENT: '已发送',
  SETTLED: '已结算',
  UNKNOWN: '待确认',
  WECHAT_SUBSCRIBE: '微信订阅消息',
  WORKBENCH: '工作台消息',
  XLSX: 'Excel 文件',
  APP_CITY_PERFORMANCE: '城市经营报表',
  ALLOW: '允许',
  DENY: '拒绝',
  MASK: '脱敏',
  DRAFT: '草稿',
  BUILD_REQUESTED: '等待构建',
  BUILT: '构建完成',
  EXTERNAL_VALIDATION_PENDING: '等待微信平台验证',
  UPLOADED: '已上传微信平台',
  UNDER_REVIEW: '微信审核中',
  RELEASED: '已发布',
  ROLLED_BACK: '已回滚',
  PASSED: '通过',
  SUCCEEDED: '成功',
};

const datasetFields: Record<string, string[]> = {
  ORDER_DETAIL: [
    'orderNo',
    'status',
    'cityId',
    'productId',
    'skuId',
    'salesAmountCents',
    'discountAmountCents',
  ],
  ORDER_PROFIT: ['orderNo', 'cityId', 'productId', 'skuId', 'salesAmountCents'],
  PAYMENT_RECONCILIATION: ['batchNo', 'transactionDate', 'status'],
  CHANNEL_SETTLEMENT: ['batchNo', 'channelId', 'periodStart', 'periodEnd', 'status'],
  MEMBER_SUMMARY: ['memberNo', 'status', 'orderCount'],
  APP_CITY_PERFORMANCE: ['cityId', 'orderCount'],
};

const messageNames: Record<string, string> = {
  AFTER_SALES_CLOSED: '售后处理完成通知',
  AFTER_SALES_OPENED: '售后申请通知',
  FULFILLMENT_READY: '行程准备就绪提醒',
  ORDER_COMPLETED: '行程完成通知',
  ORDER_CONFIRMED: '订单确认通知',
  PAYMENT_CONFIRMED: '支付成功通知',
  PAYMENT_FAILED: '支付失败提醒',
  REFUND_SUCCEEDED: '退款成功通知',
};

function safe(value: unknown, fallback = '—') {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

function asRecord(value: unknown): JsonRecord {
  return isRecord(value) ? value : {};
}

function businessName(value: unknown, fallback: string) {
  const cleaned = safe(value, fallback)
    .replace(/\s*[（([][^）)\]]*(?:dev[\s/-]*test|fixture|开发测试)[^）)\]]*[）)\]]/giu, '')
    .replace(/\b(?:dev[\s/-]*test|fixture|synthetic)\b/giu, '')
    .replace(/\s{2,}/gu, ' ')
    .trim();
  return !cleaned || /^merchant$/iu.test(cleaned) ? fallback : cleaned;
}

function messageName(row: JsonRecord) {
  const code = safe(row.code, '');
  const configured = businessName(row.name, '');
  if (!configured || configured === code || /^[A-Z][A-Z0-9_]+$/u.test(configured)) {
    return messageNames[code] ?? '业务消息通知';
  }
  return configured;
}

function amount(value: unknown) {
  const cents = Number(value ?? 0);
  if (!Number.isFinite(cents)) return '—';
  return `${cents < 0 ? '-' : ''}¥${(Math.abs(cents) / 100).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function when(value: unknown, includeTime = false) {
  if (typeof value !== 'string' && !(value instanceof Date)) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(parsed);
}

function label(value: unknown, fallback = '待确认') {
  return labels[safe(value, '')] ?? fallback;
}

function maskReference(value: unknown) {
  const text = safe(value, '');
  if (!text) return '匿名会员';
  if (text.length <= 4) return `会员 · ${text.slice(-1)}`;
  return `会员 · ****${text.slice(-4)}`;
}

function nested(row: JsonRecord, key: string) {
  return isRecord(row[key]) ? row[key] : {};
}

function useTask<T>(key: string, loader: () => Promise<T>, dependency: unknown): LoadState<T> {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<Omit<LoadState<T>, 'reload'>>({ loading: true });
  useEffect(() => {
    let active = true;
    setState({ loading: true });
    void loader().then(
      (data) => active && setState({ loading: false, data }),
      (error: unknown) => active && setState({ loading: false, error }),
    );
    return () => {
      active = false;
    };
  }, [dependency, key, revision]);
  return { ...state, reload: () => setRevision((value) => value + 1) };
}

function Header({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="r4-heading">
      <div>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      {actions && <div className="r4-actions">{actions}</div>}
    </div>
  );
}

function Notice({ children, warning = false }: { children: React.ReactNode; warning?: boolean }) {
  return <div className={`r4-notice${warning ? ' warning' : ''}`}>{children}</div>;
}

function Metrics({
  items,
}: {
  items: Array<{ label: string; value: React.ReactNode; note: string }>;
}) {
  return (
    <div className="r4-metrics r6-metrics">
      {items.map((item) => (
        <article key={item.label}>
          <span>{item.label}</span>
          <strong>{item.value}</strong>
          <small>{item.note}</small>
        </article>
      ))}
    </div>
  );
}

function Panel({
  title,
  hint,
  children,
  className = '',
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`r4-panel ${className}`.trim()}>
      <div className="r4-panel-head">
        <h2>{title}</h2>
        {hint && <span>{hint}</span>}
      </div>
      <div className="r4-panel-body">{children}</div>
    </section>
  );
}

function State({
  state,
  count,
  noun,
  children,
}: {
  state: LoadState;
  count: number;
  noun: string;
  children: React.ReactNode;
}) {
  if (state.loading) return <div className="r4-state">正在读取{noun}…</div>;
  if (state.error)
    return (
      <div className="r4-state error">
        <b>{noun}读取失败</b>
        <button onClick={state.reload}>重新加载</button>
      </div>
    );
  if (count === 0) return <div className="r4-state">当前授权范围内暂无{noun}。</div>;
  return <>{children}</>;
}

function Pill({ value }: { value: unknown }) {
  const raw = safe(value, 'UNKNOWN');
  const danger = ['FAILED', 'REJECTED', 'DIFFERENCES'].includes(raw);
  const warning = ['PENDING', 'PROCESSING', 'REVIEW_PENDING', 'UNKNOWN'].includes(raw);
  return (
    <span className={`r4-pill ${danger ? 'danger' : warning ? 'warning' : 'success'}`}>
      {label(raw)}
    </span>
  );
}

function Filters({
  search,
  setSearch,
  placeholder,
}: {
  search: string;
  setSearch: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="r4-toolbar">
      <span className="r3-locked-filter">当前授权范围</span>
      <input
        type="search"
        aria-label={placeholder}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={placeholder}
      />
      {search && <button onClick={() => setSearch('')}>清空筛选</button>}
    </div>
  );
}

function MessageTemplates({
  api,
  appId,
  state,
}: {
  api: AdminApiClient;
  appId: string;
  state: LoadState;
}) {
  const rows = unwrapList(state.data);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [channel, setChannel] = useState('WECHAT_SUBSCRIBE');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const filtered = rows.filter((row) =>
    `${safe(row.code, '')}${safe(row.name, '')}`.includes(search),
  );
  async function create() {
    if (!code.trim() || !name.trim() || busy) return;
    setBusy(true);
    setMessage('');
    try {
      await api.send(`/api/admin/m4/message-templates?appId=${encodeURIComponent(appId)}`, 'POST', {
        code: code.trim(),
        name: name.trim(),
        channel,
        providerTemplateId: '',
        subscriptionMode: 'ONE_TIME',
        contentSchema: { allowedBindings: ['order.orderNo', 'order.status'] },
      });
      setMessage('消息模板已创建');
      setShowCreate(false);
      state.reload();
    } catch {
      setMessage('创建失败，请检查权限和模板编码');
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Header
        title="消息模板"
        subtitle="消息中心 / 消息模板"
        actions={
          <button className="primary" onClick={() => setShowCreate((value) => !value)}>
            + 新建消息模板
          </button>
        }
      />
      <Notice>模板只负责内容与受控渠道配置；发送失败不会反向修改订单或支付事实。</Notice>
      <Metrics
        items={[
          { label: '模板总数', value: rows.length, note: '当前小程序范围' },
          {
            label: '启用模板',
            value: rows.filter((row) => row.status === 'ACTIVE').length,
            note: '可供业务事件使用',
          },
          {
            label: '订阅消息',
            value: rows.filter((row) => row.channel === 'WECHAT_SUBSCRIBE').length,
            note: '仍需用户订阅授权',
          },
          { label: '受控绑定', value: rows.length, note: '禁止任意脚本与任意接口' },
        ]}
      />
      {showCreate && (
        <Panel title="创建消息模板" hint="创建后仍需运营配置真实模板 ID">
          <div className="r5-form-grid two">
            <label>
              模板编码
              <input
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="例如 ORDER_CONFIRMED"
              />
            </label>
            <label>
              模板名称
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="输入业务名称"
              />
            </label>
            <label>
              发送渠道
              <select value={channel} onChange={(event) => setChannel(event.target.value)}>
                <option value="WECHAT_SUBSCRIBE">微信订阅消息</option>
                <option value="WORKBENCH">工作台消息</option>
              </select>
            </label>
          </div>
          <div className="r5-form-actions">
            <button
              className="primary"
              disabled={busy || !code.trim() || !name.trim()}
              onClick={() => void create()}
            >
              {busy ? '创建中…' : '确认创建'}
            </button>
            {message && <span>{message}</span>}
          </div>
        </Panel>
      )}
      <Panel title="模板列表" hint="数据来自当前小程序消息配置">
        <Filters search={search} setSearch={setSearch} placeholder="模板名称 / 事件编码" />
        <State state={state} count={filtered.length} noun="消息模板">
          <div className="r5-table-scroll">
            <table className="r4-table">
              <thead>
                <tr>
                  <th>模板名称</th>
                  <th>发送渠道</th>
                  <th>订阅方式</th>
                  <th>版本</th>
                  <th>状态</th>
                  <th>最近更新</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={safe(row.id)}>
                    <td>
                      <b>{messageName(row)}</b>
                      <small>{safe(row.code, '未配置事件编码')}</small>
                    </td>
                    <td>{label(row.channel, '受控消息渠道')}</td>
                    <td>{label(row.subscriptionMode, '按模板规则')}</td>
                    <td>v{Number(row.version ?? 1)}</td>
                    <td>
                      <Pill value={row.status} />
                    </td>
                    <td>{when(row.updatedAt, true)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
    </>
  );
}

function MessageTasks({
  api,
  appId,
  state,
}: {
  api: AdminApiClient;
  appId: string;
  state: LoadState;
}) {
  const rows = unwrapList(state.data);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<JsonRecord | null>(null);
  const [reason, setReason] = useState('');
  const [resolution, setResolution] = useState('CONFIRMED_NOT_SENT');
  const [busy, setBusy] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const filtered = rows.filter((row) => {
    const template = nested(row, 'template');
    return `${safe(template.name, '')}${safe(template.code, '')}${safe(row.status, '')}`.includes(
      search,
    );
  });
  const sent = rows.filter((row) => row.status === 'SENT').length;
  const failed = rows.filter((row) => row.status === 'FAILED').length;
  async function act(command: 'retry' | 'resolve') {
    if (!selected || !reason.trim() || busy) return;
    setBusy(true);
    setActionMessage('');
    try {
      await api.request(
        `/api/admin/m4/message-tasks/${encodeURIComponent(safe(selected.id))}/${command}?appId=${encodeURIComponent(appId)}`,
        {
          method: 'POST',
          headers: { 'idempotency-key': crypto.randomUUID() },
          body: JSON.stringify(
            command === 'retry' ? { reason: reason.trim() } : { reason: reason.trim(), resolution },
          ),
        },
      );
      setActionMessage(
        command === 'retry' ? '任务已重新进入安全发送队列。' : '人工核查结论已登记。',
      );
      setSelected(null);
      setReason('');
      state.reload();
    } catch (error) {
      setActionMessage(error instanceof Error ? error.message : '处置失败，请检查任务状态和权限。');
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Header title="消息发送记录" subtitle="消息中心 / 发送记录" />
      <Notice warning>
        失败任务按服务端重试策略执行；达到阈值后进入人工处理，不允许消息失败反向修改业务事实。
      </Notice>
      <Metrics
        items={[
          { label: '发送任务', value: rows.length, note: '当前小程序范围' },
          {
            label: '已送达',
            value: sent,
            note: rows.length ? `${((sent / rows.length) * 100).toFixed(1)}%` : '暂无任务',
          },
          {
            label: '处理中',
            value: rows.filter((row) => ['PENDING', 'PROCESSING'].includes(safe(row.status, '')))
              .length,
            note: '由异步 Worker 处理',
          },
          { label: '失败', value: failed, note: '保留错误摘要供人工复核' },
        ]}
      />
      <Panel title="发送明细" hint="按创建时间倒序">
        <Filters search={search} setSearch={setSearch} placeholder="模板名称 / 发送状态" />
        <State state={state} count={filtered.length} noun="消息发送记录">
          <div className="r5-table-scroll">
            <table className="r4-table">
              <thead>
                <tr>
                  <th>模板 / 事件</th>
                  <th>目标对象</th>
                  <th>渠道</th>
                  <th>尝试次数</th>
                  <th>发送结果</th>
                  <th>创建时间</th>
                  <th>异常摘要</th>
                  <th>人工处置</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const template = nested(row, 'template');
                  const deliveries = Array.isArray(row.deliveries)
                    ? row.deliveries.filter(isRecord)
                    : [];
                  return (
                    <tr key={safe(row.id)}>
                      <td>
                        <b>{messageName(template)}</b>
                        <small>{safe(template.code, '受控业务事件')}</small>
                      </td>
                      <td>{maskReference(row.recipientRef)}</td>
                      <td>{label(template.channel ?? row.channel, '受控消息渠道')}</td>
                      <td>{Number(row.attemptCount ?? deliveries.length ?? 0)}</td>
                      <td>
                        <Pill value={row.status} />
                      </td>
                      <td>{when(row.createdAt, true)}</td>
                      <td>
                        {safe(row.lastError, row.status === 'FAILED' ? '请查看服务端审计' : '—')}
                      </td>
                      <td>
                        {['FAILED', 'DEAD', 'UNCERTAIN'].includes(safe(row.status, '')) ? (
                          <button
                            className="r5-link-button"
                            onClick={() => {
                              setSelected(row);
                              setActionMessage('');
                            }}
                          >
                            复核处置
                          </button>
                        ) : (
                          '无需人工处置'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
      {selected && (
        <Panel title="消息异常人工处置" hint={`任务 ${safe(selected.id)}`}>
          <Notice warning>
            只有确认过服务端任务状态和渠道记录后才能重试或结案；所有操作均使用幂等键并写审计。
          </Notice>
          <div className="r5-form-grid two">
            <label>
              核查说明
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="填写渠道核查结果、失败原因或结案依据"
              />
            </label>
            <label>
              结案结论
              <select value={resolution} onChange={(event) => setResolution(event.target.value)}>
                <option value="CONFIRMED_NOT_SENT">确认未发送，可重新处理</option>
                <option value="CONFIRMED_SENT">确认已经发送</option>
                <option value="NO_DELIVERY_FINAL">确认不再发送并结案</option>
              </select>
            </label>
          </div>
          <div className="r5-form-actions">
            <button
              disabled={
                busy || !reason.trim() || !['FAILED', 'DEAD'].includes(safe(selected.status, ''))
              }
              onClick={() => void act('retry')}
            >
              安全重试
            </button>
            <button
              className="primary"
              disabled={busy || !reason.trim()}
              onClick={() => void act('resolve')}
            >
              登记结论
            </button>
            <button disabled={busy} onClick={() => setSelected(null)}>
              取消
            </button>
          </div>
          {actionMessage && <small className="r4-help">{actionMessage}</small>}
        </Panel>
      )}
    </>
  );
}

function Subscriptions({ state }: { state: LoadState }) {
  const rows = unwrapList(state.data);
  const accepted = rows.filter((row) => row.status === 'ACCEPTED').length;
  return (
    <>
      <Header title="用户订阅状态" subtitle="消息中心 / 用户订阅状态" />
      <Notice>系统只记录用户在具体小程序、具体模板上的订阅结果；未授权用户不能被强制推送。</Notice>
      <Metrics
        items={[
          { label: '订阅记录', value: rows.length, note: '当前小程序范围' },
          {
            label: '用户同意',
            value: accepted,
            note: rows.length ? `${((accepted / rows.length) * 100).toFixed(1)}%` : '暂无记录',
          },
          {
            label: '用户拒绝',
            value: rows.filter((row) => row.status === 'REJECTED').length,
            note: '仅记录，不反复打扰',
          },
          {
            label: '待确认',
            value: rows.filter((row) => !['ACCEPTED', 'REJECTED'].includes(safe(row.status, '')))
              .length,
            note: '等待合法触发',
          },
        ]}
      />
      <Panel title="订阅结果明细" hint="会员标识在展示层脱敏">
        <State state={state} count={rows.length} noun="用户订阅记录">
          <div className="r5-table-scroll">
            <table className="r4-table">
              <thead>
                <tr>
                  <th>会员</th>
                  <th>消息模板</th>
                  <th>最近订阅结果</th>
                  <th>订阅来源</th>
                  <th>最近更新时间</th>
                  <th>有效期</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const template = nested(row, 'template');
                  return (
                    <tr key={safe(row.id)}>
                      <td>{maskReference(row.customerOwnerKey)}</td>
                      <td>
                        <b>{messageName(template)}</b>
                        <small>{safe(template.code, '受控模板')}</small>
                      </td>
                      <td>
                        <Pill value={row.status} />
                      </td>
                      <td>{safe(row.source, '小程序用户授权')}</td>
                      <td>{when(row.updatedAt, true)}</td>
                      <td>{when(row.expiresAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
    </>
  );
}

function ProfitReport({ profit, cities }: { profit: LoadState; cities: LoadState }) {
  const rows = unwrapList(profit.data);
  const cityMap = new Map(
    unwrapList(cities.data).map((row) => [safe(row.id, ''), safe(row.name, '未命名城市')]),
  );
  const total = (key: string) => rows.reduce((sum, row) => sum + Number(row[key] ?? 0), 0);
  const costs = [
    ['用车成本', total('vehicleCostCents')],
    ['票务成本', total('ticketCostCents')],
    ['向导成本', total('guideCostCents')],
    ['渠道佣金', total('channelCommissionCents')],
  ] as const;
  const maxCost = Math.max(1, ...costs.map(([, value]) => value));
  return (
    <>
      <Header
        title="订单利润报表"
        subtitle="财务与报表 / 订单利润"
        actions={
          <a className="r6-button" href={pathFor('ADM-I08')}>
            导出财务明细
          </a>
        }
      />
      <Notice>利润读取下单时财务规则快照；历史订单不会因后续成本配置变化而重新漂移。</Notice>
      <Metrics
        items={[
          { label: '销售额', value: amount(total('salesAmountCents')), note: '当前授权范围' },
          { label: '实付', value: amount(total('paidAmountCents')), note: '支付成功事实' },
          { label: '退款', value: amount(total('refundAmountCents')), note: '成功退款事实' },
          {
            label: '总成本',
            value: amount(costs.reduce((sum, [, value]) => sum + value, 0)),
            note: '规则快照汇总',
          },
          { label: '渠道佣金', value: amount(total('channelCommissionCents')), note: '已确认账本' },
          { label: '毛利', value: amount(total('grossProfitCents')), note: '仅 L2 授权可见' },
        ]}
      />
      <div className="r6-report-grid">
        <Panel title="订单毛利分布" hint="真实订单快照">
          <State state={profit} count={rows.length} noun="订单利润">
            <div className="r6-bars">
              {rows.slice(0, 8).map((row, index) => (
                <div
                  key={`${safe(row.id, safe(row.orderId, 'profit'))}-${safe(row.orderNo, 'order')}-${index}`}
                >
                  <span>{safe(row.orderNo, '订单')}</span>
                  <i
                    style={{
                      width: `${Math.max(4, Math.min(100, (Number(row.grossProfitCents ?? 0) / Math.max(1, total('grossProfitCents'))) * 100))}%`,
                    }}
                  />
                  <b>{amount(row.grossProfitCents)}</b>
                </div>
              ))}
            </div>
          </State>
        </Panel>
        <Panel title="成本结构" hint="不代表数量库存">
          <div className="r6-bars">
            {costs.map(([name, value]) => (
              <div key={name}>
                <span>{name}</span>
                <i style={{ width: `${Math.max(4, (value / maxCost) * 100)}%` }} />
                <b>{amount(value)}</b>
              </div>
            ))}
          </div>
        </Panel>
      </div>
      <Panel title="订单利润明细" hint="金额按元展示">
        <State state={profit} count={rows.length} noun="利润快照">
          <div className="r5-table-scroll">
            <table className="r4-table">
              <thead>
                <tr>
                  <th>订单</th>
                  <th>城市</th>
                  <th>销售额</th>
                  <th>实付</th>
                  <th>退款</th>
                  <th>用车成本</th>
                  <th>票务成本</th>
                  <th>向导成本</th>
                  <th>渠道佣金</th>
                  <th>毛利</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr
                    key={`${safe(row.id, safe(row.orderId, 'profit'))}-${safe(row.orderNo, 'order')}-${index}`}
                  >
                    <td>{safe(row.orderNo, '订单')}</td>
                    <td>{cityMap.get(safe(row.cityId, '')) ?? '城市配置待补充'}</td>
                    <td>{amount(row.salesAmountCents)}</td>
                    <td>{amount(row.paidAmountCents)}</td>
                    <td>{amount(row.refundAmountCents)}</td>
                    <td>{amount(row.vehicleCostCents)}</td>
                    <td>{amount(row.ticketCostCents)}</td>
                    <td>{amount(row.guideCostCents)}</td>
                    <td>{amount(row.channelCommissionCents)}</td>
                    <td>
                      <b className="r6-positive">{amount(row.grossProfitCents)}</b>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
    </>
  );
}

function Reconciliation({
  api,
  appId,
  state,
  merchants,
}: {
  api: AdminApiClient;
  appId: string;
  state: LoadState;
  merchants: LoadState;
}) {
  const rows = unwrapList(state.data);
  const merchantRows = unwrapList(merchants.data);
  const [merchantId, setMerchantId] = useState('');
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  async function run() {
    const selected = merchantId || safe(merchantRows[0]?.id, '');
    if (!selected || busy) return;
    setBusy(true);
    try {
      await api.send(`/api/admin/m7/apps/${encodeURIComponent(appId)}/reconciliation/run`, 'POST', {
        merchantId: selected,
        transactionDate,
        idempotencyKey: `reconcile-${appId}-${selected}-${transactionDate}`,
      });
      setMessage('对账任务已执行');
      state.reload();
    } catch {
      setMessage('对账执行失败，请检查账单适配器和权限');
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Header title="支付对账" subtitle="财务与报表 / 支付对账" />
      <Notice warning>
        支付成功与退款事实以服务端查询及回调为准；差异只进入人工核验，不直接改写订单状态。
      </Notice>
      <Metrics
        items={[
          { label: '对账批次', value: rows.length, note: '当前小程序范围' },
          {
            label: '系统交易',
            value: rows.reduce((sum, row) => sum + Number(row.systemCount ?? 0), 0),
            note: '持久化支付与退款事实',
          },
          {
            label: '渠道交易',
            value: rows.reduce((sum, row) => sum + Number(row.providerCount ?? 0), 0),
            note: '受控账单适配器',
          },
          {
            label: '差异记录',
            value: rows.reduce((sum, row) => sum + Number(row.differenceCount ?? 0), 0),
            note: '待人工核验',
          },
          {
            label: '已完成对账',
            value: rows.filter((row) =>
              ['MATCHED', 'REVIEWED', 'COMPLETED'].includes(safe(row.status, '')),
            ).length,
            note: '自动或人工闭环',
          },
        ]}
      />
      <Panel title="执行对账" hint="按交易日与支付商户核对">
        <div className="r4-toolbar">
          <input
            type="date"
            value={transactionDate}
            onChange={(event) => setTransactionDate(event.target.value)}
          />
          <select
            aria-label="支付商户"
            value={merchantId}
            onChange={(event) => setMerchantId(event.target.value)}
          >
            <option value="">选择支付商户</option>
            {merchantRows.map((row) => (
              <option key={safe(row.id)} value={safe(row.id)}>
                {businessName(row.displayName, '支付商户')}
              </option>
            ))}
          </select>
          <button
            className="primary"
            disabled={busy || merchantRows.length === 0}
            onClick={() => void run()}
          >
            {busy ? '执行中…' : '执行对账'}
          </button>
          {message && <span>{message}</span>}
        </div>
      </Panel>
      <Panel title="对账批次" hint="金额按元展示">
        <State state={state} count={rows.length} noun="对账批次">
          <div className="r5-table-scroll">
            <table className="r4-table">
              <thead>
                <tr>
                  <th>对账批次</th>
                  <th>交易日</th>
                  <th>渠道来源</th>
                  <th>系统交易</th>
                  <th>渠道交易</th>
                  <th>差异记录</th>
                  <th>差异金额</th>
                  <th>状态</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={safe(row.id)}>
                    <td>{safe(row.batchNo, '对账批次')}</td>
                    <td>{when(row.transactionDate)}</td>
                    <td>{safe(row.providerSource, '微信支付账单')}</td>
                    <td>
                      {Number(row.systemCount ?? 0)} 笔 / {amount(row.systemAmountCents)}
                    </td>
                    <td>
                      {Number(row.providerCount ?? 0)} 笔 / {amount(row.providerAmountCents)}
                    </td>
                    <td>{Number(row.differenceCount ?? 0)}</td>
                    <td className={Number(row.differenceCents ?? 0) !== 0 ? 'r6-danger' : ''}>
                      {amount(row.differenceCents)}
                    </td>
                    <td>
                      <Pill value={row.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
    </>
  );
}

function SettlementFinance({
  api,
  appId,
  state,
  channels,
}: {
  api: AdminApiClient;
  appId: string;
  state: LoadState;
  channels: LoadState;
}) {
  const rows = unwrapList(state.data);
  const channelMap = new Map(
    unwrapList(channels.data).map((row) => [safe(row.id, ''), safe(row.name, '未命名渠道')]),
  );
  const [busyId, setBusyId] = useState('');
  const [message, setMessage] = useState('');
  const [proofDrafts, setProofDrafts] = useState<
    Record<string, { file?: File; transferReference: string; transferredAt: string }>
  >({});
  async function transition(row: JsonRecord) {
    const status = safe(row.status, '');
    const action = status === 'DRAFT' ? 'submit' : status === 'REVIEW_PENDING' ? 'approve' : '';
    if (!action) return;
    setBusyId(safe(row.id));
    try {
      await api.send(
        `/api/admin/m6/apps/${encodeURIComponent(appId)}/settlement-batches/${encodeURIComponent(safe(row.id))}/${action}`,
        'POST',
        { expectedVersion: Number(row.version ?? 0) },
      );
      setMessage('结算状态已更新');
      state.reload();
    } catch {
      setMessage('操作失败：请检查四眼复核、状态和权限');
    } finally {
      setBusyId('');
    }
  }
  async function uploadProof(row: JsonRecord) {
    const id = safe(row.id);
    const draft = proofDrafts[id];
    if (!draft?.file || !draft.transferReference || !draft.transferredAt) {
      setMessage('请先选择 PDF/JPG/PNG 凭证，并填写打款流水号和时间');
      return;
    }
    const form = new FormData();
    form.set('file', draft.file);
    form.set('expectedBatchVersion', String(Number(row.version ?? 0)));
    form.set('amountCents', String(Number(row.payableAmountCents ?? 0)));
    form.set('currency', safe(row.currency, 'CNY'));
    form.set('transferReference', draft.transferReference);
    form.set('transferredAt', new Date(draft.transferredAt).toISOString());
    setBusyId(id);
    try {
      await api.upload(
        `/api/admin/m6/apps/${encodeURIComponent(appId)}/settlement-batches/${encodeURIComponent(id)}/offline-transfer-proofs`,
        form,
        `offline-proof:${id}:${Date.now()}`,
      );
      setMessage('线下打款凭证已上传，等待另一名员工复核');
      state.reload();
    } catch {
      setMessage('凭证上传失败：金额必须与应结金额完全一致，文件限 PDF/JPG/PNG 且不超过 10MB');
    } finally {
      setBusyId('');
    }
  }
  async function reviewProof(row: JsonRecord, proof: JsonRecord, decision: 'approve' | 'reject') {
    const reason = decision === 'reject' ? window.prompt('请输入驳回原因（必填）', '') : undefined;
    if (decision === 'reject' && !reason?.trim()) return;
    const id = safe(row.id);
    setBusyId(id);
    try {
      await api.send(
        `/api/admin/m6/apps/${encodeURIComponent(appId)}/settlement-batches/${encodeURIComponent(id)}/offline-transfer-proofs/${encodeURIComponent(safe(proof.id))}/${decision}`,
        'POST',
        {
          expectedProofVersion: Number(proof.version ?? 0),
          ...(reason ? { rejectionReason: reason.trim() } : {}),
        },
      );
      setMessage(decision === 'approve' ? '凭证复核通过，结算已完成' : '凭证已驳回，可重新上传');
      state.reload();
    } catch {
      setMessage('凭证复核失败：上传人与复核人必须为不同员工，并检查当前状态');
    } finally {
      setBusyId('');
    }
  }
  async function downloadProof(row: JsonRecord, proof: JsonRecord) {
    const id = safe(row.id);
    setBusyId(id);
    try {
      const grant = unwrapRecord(
        await api.request(
          `/api/admin/m6/apps/${encodeURIComponent(appId)}/settlement-batches/${encodeURIComponent(id)}/offline-transfer-proofs/${encodeURIComponent(safe(proof.id))}/download-grants`,
          {
            method: 'POST',
            headers: { 'idempotency-key': `proof-download:${safe(proof.id)}:${Date.now()}` },
          },
        ),
      );
      const blob = await api.download(
        `/api/admin/m6/apps/${encodeURIComponent(appId)}/settlement-batches/${encodeURIComponent(id)}/offline-transfer-proofs/${encodeURIComponent(safe(proof.id))}/content`,
        { 'x-download-grant': safe(grant.token) },
      );
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = safe(asRecord(proof.file).originalName, '打款凭证');
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      setMessage('凭证下载授权失败或已过期，请重新获取');
    } finally {
      setBusyId('');
    }
  }
  return (
    <>
      <Header title="渠道结算" subtitle="财务与报表 / 渠道结算" />
      <Notice>
        首版仅支持线下打款。系统不会自动付款；打款金额必须与应结金额完全一致，上传人与凭证复核人必须为不同员工，所有操作均留痕。
      </Notice>
      <Metrics
        items={[
          { label: '结算批次', value: rows.length, note: '当前小程序范围' },
          {
            label: '待结算金额',
            value: amount(
              rows
                .filter((row) => !['SETTLED'].includes(safe(row.status, '')))
                .reduce((sum, row) => sum + Number(row.payableAmountCents ?? 0), 0),
            ),
            note: '未完成批次',
          },
          {
            label: '已结算金额',
            value: amount(
              rows
                .filter((row) => row.status === 'SETTLED')
                .reduce((sum, row) => sum + Number(row.payableAmountCents ?? 0), 0),
            ),
            note: '已完成批次',
          },
          {
            label: '退款冲销',
            value: amount(rows.reduce((sum, row) => sum + Number(row.reversalAmountCents ?? 0), 0)),
            note: '负向佣金流水',
          },
          {
            label: '异常待复核',
            value: rows.filter((row) => row.status === 'REVIEW_PENDING').length,
            note: '禁止同人自审',
          },
        ]}
      />
      <Panel title="结算批次" hint="财务动作由服务端状态机校验">
        <State state={state} count={rows.length} noun="渠道结算批次">
          <div className="r5-table-scroll">
            <table className="r4-table">
              <thead>
                <tr>
                  <th>结算批次</th>
                  <th>渠道组织</th>
                  <th>结算周期</th>
                  <th>确认佣金</th>
                  <th>退款冲销</th>
                  <th>应结算</th>
                  <th>状态</th>
                  <th>线下凭证</th>
                  <th>财务操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const status = safe(row.status, '');
                  const actionText =
                    status === 'DRAFT' ? '提交复核' : status === 'REVIEW_PENDING' ? '复核通过' : '';
                  const proofs = Array.isArray(row.offlineTransferProofs)
                    ? row.offlineTransferProofs.map(asRecord)
                    : [];
                  const currentProof = proofs[0];
                  const draft = proofDrafts[safe(row.id)] ?? {
                    transferReference: '',
                    transferredAt: '',
                  };
                  return (
                    <tr key={safe(row.id)}>
                      <td>{safe(row.batchNo, '结算批次')}</td>
                      <td>{channelMap.get(safe(row.channelId, '')) ?? '渠道配置待补充'}</td>
                      <td>
                        {when(row.periodStart)} 至 {when(row.periodEnd)}
                      </td>
                      <td>{amount(row.grossCommissionCents)}</td>
                      <td>{amount(row.reversalAmountCents)}</td>
                      <td>
                        <b>{amount(row.payableAmountCents)}</b>
                      </td>
                      <td>
                        <Pill value={row.status} />
                      </td>
                      <td>
                        {status === 'APPROVED' ? (
                          <div className="r6-proof-form">
                            <input
                              aria-label={`选择 ${safe(row.batchNo)} 打款凭证`}
                              type="file"
                              accept="application/pdf,image/jpeg,image/png"
                              onChange={(event) => {
                                const file = event.target.files?.[0];
                                setProofDrafts((current) => ({
                                  ...current,
                                  [safe(row.id)]: { ...draft, ...(file ? { file } : {}) },
                                }));
                              }}
                            />
                            <input
                              aria-label="打款流水号"
                              placeholder="打款流水号"
                              value={draft.transferReference}
                              onChange={(event) =>
                                setProofDrafts((current) => ({
                                  ...current,
                                  [safe(row.id)]: {
                                    ...draft,
                                    transferReference: event.target.value,
                                  },
                                }))
                              }
                            />
                            <input
                              aria-label="打款时间"
                              type="datetime-local"
                              value={draft.transferredAt}
                              onChange={(event) =>
                                setProofDrafts((current) => ({
                                  ...current,
                                  [safe(row.id)]: { ...draft, transferredAt: event.target.value },
                                }))
                              }
                            />
                            <button
                              className="link-button"
                              disabled={busyId === safe(row.id)}
                              onClick={() => void uploadProof(row)}
                            >
                              上传凭证
                            </button>
                          </div>
                        ) : currentProof ? (
                          <div>
                            <Pill value={currentProof.status} />
                            <small>
                              {safe(asRecord(currentProof.file).originalName, '凭证文件')}
                            </small>
                            <button
                              className="link-button"
                              disabled={busyId === safe(row.id)}
                              onClick={() => void downloadProof(row, currentProof)}
                            >
                              下载核验
                            </button>
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {status === 'PROCESSING' && currentProof?.status === 'PENDING_REVIEW' ? (
                          <>
                            <button
                              className="link-button"
                              disabled={busyId === safe(row.id)}
                              onClick={() => void reviewProof(row, currentProof, 'approve')}
                            >
                              复核通过
                            </button>
                            <button
                              className="link-button"
                              disabled={busyId === safe(row.id)}
                              onClick={() => void reviewProof(row, currentProof, 'reject')}
                            >
                              驳回
                            </button>
                          </>
                        ) : actionText ? (
                          <button
                            className="link-button"
                            disabled={busyId === safe(row.id)}
                            onClick={() => void transition(row)}
                          >
                            {busyId === safe(row.id) ? '提交中…' : actionText}
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </State>
        {message && <p className="command-result">{message}</p>}
      </Panel>
    </>
  );
}

function PerformanceReport({ state, cities }: { state: LoadState; cities: LoadState }) {
  const rows = unwrapList(state.data);
  const cityMap = new Map(
    unwrapList(cities.data).map((row) => [safe(row.id, ''), safe(row.name, '未命名城市')]),
  );
  const total = (key: string) => rows.reduce((sum, row) => sum + Number(row[key] ?? 0), 0);
  const max = Math.max(1, ...rows.map((row) => Number(row.paidAmountCents ?? 0)));
  return (
    <>
      <Header
        title="小程序城市经营报表"
        subtitle="财务与报表 / 经营分析"
        actions={
          <a className="r6-button" href={pathFor('ADM-I08')}>
            保存为导出任务
          </a>
        }
      />
      <Notice>经营分析默认按 App 与城市双维度隔离；页面指标受当前用户数据范围限制。</Notice>
      <Metrics
        items={[
          { label: '城市维度', value: rows.length, note: '当前 App 授权范围' },
          { label: '支付订单', value: total('orderCount'), note: '服务端聚合' },
          { label: '实付 GMV', value: amount(total('paidAmountCents')), note: '支付成功事实' },
          { label: '退款金额', value: amount(total('refundAmountCents')), note: '成功退款事实' },
          { label: '毛利', value: amount(total('grossProfitCents')), note: '仅 L2 授权可见' },
        ]}
      />
      <div className="r6-report-grid">
        <Panel title="城市 GMV 对比" hint="真实聚合">
          <State state={state} count={rows.length} noun="城市经营数据">
            <div className="r6-bars">
              {rows.map((row) => (
                <div key={safe(row.cityId)}>
                  <span>{cityMap.get(safe(row.cityId, '')) ?? '城市配置待补充'}</span>
                  <i
                    style={{
                      width: `${Math.max(4, (Number(row.paidAmountCents ?? 0) / max) * 100)}%`,
                    }}
                  />
                  <b>{amount(row.paidAmountCents)}</b>
                </div>
              ))}
            </div>
          </State>
        </Panel>
        <Panel title="经营健康" hint="当前范围">
          <dl className="r6-kv">
            <div>
              <dt>订单总数</dt>
              <dd>{total('orderCount')}</dd>
            </div>
            <div>
              <dt>平均客单</dt>
              <dd>
                {amount(total('orderCount') ? total('paidAmountCents') / total('orderCount') : 0)}
              </dd>
            </div>
            <div>
              <dt>退款率</dt>
              <dd>
                {total('paidAmountCents')
                  ? `${((total('refundAmountCents') / total('paidAmountCents')) * 100).toFixed(2)}%`
                  : '—'}
              </dd>
            </div>
            <div>
              <dt>毛利率</dt>
              <dd>
                {total('paidAmountCents')
                  ? `${((total('grossProfitCents') / total('paidAmountCents')) * 100).toFixed(2)}%`
                  : '—'}
              </dd>
            </div>
          </dl>
        </Panel>
      </div>
      <Panel title="城市经营明细" hint="可继续下钻到后续产品/SKU报表">
        <State state={state} count={rows.length} noun="城市经营数据">
          <div className="r5-table-scroll">
            <table className="r4-table">
              <thead>
                <tr>
                  <th>小程序 / 城市</th>
                  <th>支付订单</th>
                  <th>实付 GMV</th>
                  <th>退款金额</th>
                  <th>客单价</th>
                  <th>毛利</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={safe(row.cityId)}>
                    <td>{cityMap.get(safe(row.cityId, '')) ?? '城市配置待补充'}</td>
                    <td>{Number(row.orderCount ?? 0)}</td>
                    <td>{amount(row.paidAmountCents)}</td>
                    <td>{amount(row.refundAmountCents)}</td>
                    <td>
                      {amount(
                        Number(row.orderCount ?? 0)
                          ? Number(row.paidAmountCents ?? 0) / Number(row.orderCount)
                          : 0,
                      )}
                    </td>
                    <td>{amount(row.grossProfitCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
    </>
  );
}

function ExportCenter({
  api,
  appId,
  state,
}: {
  api: AdminApiClient;
  appId: string;
  state: LoadState;
}) {
  const rows = unwrapList(state.data);
  const [dataset, setDataset] = useState('ORDER_DETAIL');
  const [format, setFormat] = useState('XLSX');
  const [selectedFields, setSelectedFields] = useState(datasetFields.ORDER_DETAIL ?? []);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  async function create() {
    if (busy || selectedFields.length === 0) return;
    setBusy(true);
    try {
      await api.send(`/api/admin/m7/apps/${encodeURIComponent(appId)}/exports`, 'POST', {
        dataset,
        format,
        fields: selectedFields,
        filters: {},
        idempotencyKey: `admin-export-${dataset}-${Date.now()}`,
      });
      setMessage('导出任务已提交');
      state.reload();
    } catch {
      setMessage('提交失败：请检查导出权限和字段策略');
    } finally {
      setBusy(false);
    }
  }
  async function download(row: JsonRecord) {
    if (row.status !== 'READY') return;
    try {
      const id = encodeURIComponent(safe(row.id));
      const grant = unwrapRecord(
        await api.send(
          `/api/admin/m7/apps/${encodeURIComponent(appId)}/exports/${id}/download-grant`,
          'POST',
        ),
      );
      const blob = await api.download(
        `/api/admin/m7/apps/${encodeURIComponent(appId)}/exports/${id}/download?token=${encodeURIComponent(safe(grant.token))}`,
      );
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = safe(row.fileName, 'export');
      anchor.click();
      URL.revokeObjectURL(url);
      setMessage('下载已开始；链接到期后需重新申请');
    } catch {
      setMessage('下载失败，请刷新任务状态后重试');
    }
  }
  return (
    <>
      <Header title="自定义导出中心" subtitle="财务与报表 / 自定义导出" />
      <Panel title="创建导出任务" hint="选择数据、格式与导出字段">
        <div className="operations-form-inline">
          <div className="r5-form-grid">
            <label>
              数据集
              <select
                value={dataset}
                onChange={(event) => {
                  setDataset(event.target.value);
                  setSelectedFields(datasetFields[event.target.value] ?? []);
                }}
              >
                {Object.keys(datasetFields).map((key) => (
                  <option key={key} value={key}>
                    {label(key)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              文件格式
              <select value={format} onChange={(event) => setFormat(event.target.value)}>
                <option value="XLSX">Excel 文件</option>
                <option value="CSV">CSV 文件</option>
              </select>
            </label>
          </div>
          <div className="r5-form-actions">
            <button
              className="primary"
              disabled={busy || selectedFields.length === 0}
              onClick={() => void create()}
            >
              {busy ? '提交中…' : '生成导出文件'}
            </button>
          </div>
        </div>
        <fieldset className="operations-field-choices">
          <legend>导出字段 · 已选 {selectedFields.length} 项</legend>
          {(datasetFields[dataset] ?? []).map((field) => (
            <label key={field}>
              <input
                type="checkbox"
                checked={selectedFields.includes(field)}
                onChange={(event) =>
                  setSelectedFields((current) =>
                    event.target.checked
                      ? [...current, field]
                      : current.filter((item) => item !== field),
                  )
                }
              />
              {fieldName(field)}
            </label>
          ))}
        </fieldset>
        <p className="operations-rule-note">
          至少选择一个字段。敏感字段须经权限校验与二次确认；下载链接仅短期有效，申请和下载均留痕。
        </p>
        {message && <Notice>{message}</Notice>}
      </Panel>
      <Panel title="导出任务">
        <State state={state} count={rows.length} noun="导出任务">
          <div className="r5-table-scroll">
            <table className="r4-table">
              <thead>
                <tr>
                  <th>数据集</th>
                  <th>文件格式</th>
                  <th>字段策略</th>
                  <th>行数</th>
                  <th>状态</th>
                  <th>创建时间</th>
                  <th>过期时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={safe(row.id)}>
                    <td>{label(row.dataset, '业务数据')}</td>
                    <td>{label(row.format, safe(row.format))}</td>
                    <td>
                      {Array.isArray(row.fields)
                        ? `${row.fields.length} 个授权字段`
                        : '按账号权限导出'}
                    </td>
                    <td>{Number(row.rowCount ?? 0)}</td>
                    <td>
                      <Pill value={row.status} />
                    </td>
                    <td>{when(row.createdAt, true)}</td>
                    <td>{when(row.expiresAt, true)}</td>
                    <td>
                      <button
                        className="link-button"
                        disabled={row.status !== 'READY'}
                        onClick={() => void download(row)}
                      >
                        {row.status === 'READY' ? '下载' : '等待生成'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
    </>
  );
}

function fieldName(value: string) {
  const names: Record<string, string> = {
    batchNo: '批次号',
    channelId: '渠道',
    cityId: '城市',
    discountAmountCents: '优惠金额',
    orderCount: '订单数',
    orderNo: '订单号',
    periodEnd: '周期结束',
    periodStart: '周期开始',
    productId: '产品',
    salesAmountCents: '销售额',
    skuId: '服务套餐',
    status: '状态',
    transactionDate: '交易日期',
    memberNo: '会员编号',
  };
  return names[value] ?? '受控字段';
}

function MessageFinanceContent({ api, targetAppId: appId, pageId }: PageProps) {
  const encoded = encodeURIComponent(appId);
  const empty = async () => ({ items: [] });
  const templates = useTask(
    `r6-templates-${appId}`,
    () =>
      pageId === 'ADM-I01' ? api.get(`/api/admin/m4/message-templates?appId=${encoded}`) : empty(),
    `${pageId}:${appId}`,
  );
  const tasks = useTask(
    `r6-tasks-${appId}`,
    () =>
      pageId === 'ADM-I02' ? api.get(`/api/admin/m4/message-tasks?appId=${encoded}`) : empty(),
    `${pageId}:${appId}`,
  );
  const subscriptions = useTask(
    `r6-subs-${appId}`,
    () =>
      pageId === 'ADM-I03'
        ? api.get(`/api/admin/m4/message-subscriptions?appId=${encoded}`)
        : empty(),
    `${pageId}:${appId}`,
  );
  const profit = useTask(
    `r6-profit-${appId}`,
    () =>
      pageId === 'ADM-I04'
        ? api.get(`/api/admin/m7/apps/${encoded}/finance/order-profit`)
        : empty(),
    `${pageId}:${appId}`,
  );
  const reconciliation = useTask(
    `r6-reconciliation-${appId}`,
    () =>
      pageId === 'ADM-I05'
        ? api.get(`/api/admin/m7/apps/${encoded}/reconciliation/batches`)
        : empty(),
    `${pageId}:${appId}`,
  );
  const settlements = useTask(
    `r6-settlements-${appId}`,
    () =>
      pageId === 'ADM-I06' ? api.get(`/api/admin/m6/apps/${encoded}/settlement-batches`) : empty(),
    `${pageId}:${appId}`,
  );
  const performance = useTask(
    `r6-performance-${appId}`,
    () =>
      pageId === 'ADM-I07'
        ? api.get(`/api/admin/m7/apps/${encoded}/reports/app-city-performance`)
        : empty(),
    `${pageId}:${appId}`,
  );
  const exportsState = useTask(
    `r6-exports-${appId}`,
    () => (pageId === 'ADM-I08' ? api.get(`/api/admin/m7/apps/${encoded}/exports`) : empty()),
    `${pageId}:${appId}`,
  );
  const cities = useTask(
    `r6-cities-${appId}`,
    () => (['ADM-I04', 'ADM-I07'].includes(pageId) ? api.get('/api/admin/m2/cities') : empty()),
    `${pageId}:${appId}`,
  );
  const channels = useTask(
    `r6-channels-${appId}`,
    () => (pageId === 'ADM-I06' ? api.get(`/api/admin/m6/apps/${encoded}/channels`) : empty()),
    `${pageId}:${appId}`,
  );
  const merchants = useTask(
    `r6-merchants-${appId}`,
    () =>
      pageId === 'ADM-I05' ? api.get(`/api/admin/m3/payment-merchants?appId=${encoded}`) : empty(),
    `${pageId}:${appId}`,
  );

  if (!appId)
    return (
      <div className="r4-state error">
        <b>请选择目标小程序</b>
        <p>消息与财务数据必须在明确的小程序范围内读取。</p>
      </div>
    );
  if (pageId === 'ADM-I01') return <MessageTemplates api={api} appId={appId} state={templates} />;
  if (pageId === 'ADM-I02') return <MessageTasks api={api} appId={appId} state={tasks} />;
  if (pageId === 'ADM-I03') return <Subscriptions state={subscriptions} />;
  if (pageId === 'ADM-I04') return <ProfitReport profit={profit} cities={cities} />;
  if (pageId === 'ADM-I05')
    return <Reconciliation api={api} appId={appId} state={reconciliation} merchants={merchants} />;
  if (pageId === 'ADM-I06')
    return <SettlementFinance api={api} appId={appId} state={settlements} channels={channels} />;
  if (pageId === 'ADM-I07') return <PerformanceReport state={performance} cities={cities} />;
  return <ExportCenter api={api} appId={appId} state={exportsState} />;
}

const scopeLabels: Record<string, string> = {
  ALL: '全部数据',
  APP: '指定小程序',
  CITY: '指定城市',
  PRODUCT: '指定产品',
  SKU: '指定服务套餐',
  CHANNEL: '指定渠道',
  ORG: '指定组织',
  ASSIGNED_ORDER: '已分配订单',
  SELF: '本人数据',
};

const identityLabels: Record<string, string> = {
  ADMIN_EXTERNAL: '后台统一身份',
  ADMIN_PASSWORD: '后台账号',
  OPS_WECHAT: '运营微信绑定',
  CUSTOMER_WECHAT: '用户微信身份',
  TEST: '测试身份',
};

const fieldLabels: Record<string, string> = {
  mobile: '联系电话',
  contactName: '联系人姓名',
  contactWechat: '联系人微信',
  costAmount: '成本金额',
  costAmountCents: '成本金额',
  commissionAmount: '佣金金额',
  commissionAmountCents: '佣金金额',
  grossProfit: '毛利',
  grossProfitCents: '毛利',
  refundAmount: '退款金额',
  refundAmountCents: '退款金额',
  paymentSecret: '支付密钥与证书',
};

const permissionLabels: Record<string, string> = {
  after_sales: '售后',
  app: '小程序',
  order: '订单',
  product: '产品',
  city: '城市',
  channel: '渠道',
  cms: '页面装修',
  commission: '佣金',
  finance: '财务',
  fulfillment: '履约',
  marketing: '营销',
  member: '会员',
  merchant: '支付商户',
  message: '消息',
  payment: '支付',
  reconciliation: '对账',
  refund: '退款',
  release: '发布',
  settlement: '结算',
  system: '系统安全',
  ops: '运维',
};

function permissionName(row: JsonRecord) {
  const configured = safe(row.description, '');
  if (
    configured &&
    !/^[A-Z0-9_.-]+$/u.test(configured) &&
    !/(?:DEV[/-]?TEST|fixture|synthetic|M\d+\s+permission)/iu.test(configured)
  )
    return configured;
  const resource = permissionLabels[safe(row.resource, '')] ?? '受控业务';
  const actionNames: Record<string, string> = {
    view: '查看',
    manage: '管理',
    export: '导出',
    refund: '退款',
    release: '发布',
    change: '变更',
    execute: '执行',
    confirm: '确认',
    complete: '完成',
    create: '创建',
    process: '处理',
    publish: '发布',
    read: '查看',
    record: '登记',
    reconcile: '对账',
    review: '复核',
  };
  return `${resource}${actionNames[safe(row.action, '')] ?? '操作'}`;
}

function roleName(row: JsonRecord) {
  const code = safe(row.code, '');
  const name = businessName(row.name, '未命名角色');
  if (/DEV.*FIXTURE/iu.test(code) || /^M\d+\s*管理员$/u.test(name)) return '本地测试管理员';
  return name;
}

function roleCode(row: JsonRecord) {
  return /DEV.*FIXTURE/iu.test(safe(row.code, '')) ? '开发测试角色' : safe(row.code, '受控角色');
}

function userName(row: JsonRecord) {
  const name = businessName(row.displayName, '未命名系统用户');
  return /^M\d+\s*客户$/u.test(name) ? '本地测试用户' : name;
}

function accountName(value: unknown) {
  const text = safe(value, '');
  if (!text) return '尚未配置登录账号';
  if (/^(?:customer|admin|operator)[-_]/iu.test(text) || /@example\.test$/iu.test(text))
    return '开发测试账号';
  if (text.length <= 4) return `账号 · ${text.slice(-1)}`;
  return `账号 · ****${text.slice(-4)}`;
}

function roleRefs(row: JsonRecord) {
  return Array.isArray(row.roles) ? row.roles.filter(isRecord) : [];
}

function permissionRefs(row: JsonRecord) {
  return Array.isArray(row.permissions) ? row.permissions.filter(isRecord) : [];
}

function arrayRecords(row: JsonRecord, key: string) {
  return Array.isArray(row[key]) ? row[key].filter(isRecord) : [];
}

function mapById(rows: JsonRecord[]) {
  return new Map(rows.map((row) => [safe(row.id, ''), row]));
}

function countOf(row: JsonRecord, key: string) {
  const counts = nested(row, '_count');
  return Number(counts[key] ?? 0);
}

function valueName(
  row: JsonRecord,
  apps: Map<string, JsonRecord>,
  cities: Map<string, JsonRecord>,
) {
  const type = safe(row.scopeType, '');
  const value = safe(row.scopeValue, '');
  if (value === '*') return '全部可用对象';
  const configured = type === 'APP' ? apps.get(value) : type === 'CITY' ? cities.get(value) : null;
  if (configured) return businessName(configured.displayName ?? configured.name, '已配置对象');
  if (['SELF', 'ASSIGNED_ORDER'].includes(type)) return scopeLabels[type];
  return `已配置${scopeLabels[type] ?? '业务'}对象`;
}

function SecurityUsers({
  api,
  appId,
  state,
}: {
  api: AdminApiClient;
  appId: string;
  state: LoadState;
}) {
  const users = unwrapList(unwrapRecord(state.data).users);
  const roles = unwrapList(unwrapRecord(state.data).roles);
  const [pending, setPending] = useState<JsonRecord>();
  const [roleUser, setRoleUser] = useState<JsonRecord>();
  const [assignmentUser, setAssignmentUser] = useState<JsonRecord>();
  const [assignmentRows, setAssignmentRows] = useState<JsonRecord[]>([]);
  const [assignmentType, setAssignmentType] = useState('CITY');
  const [assignmentId, setAssignmentId] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  async function disable() {
    if (!pending || busy) return;
    setBusy(true);
    setMessage('');
    try {
      await api.send(
        `/api/admin/m8/apps/${encodeURIComponent(appId)}/security/users/${encodeURIComponent(safe(pending.id))}/disable`,
        'POST',
      );
      setPending(undefined);
      setMessage('员工已停用，现有会话已全部失效。');
      state.reload();
    } catch {
      setMessage('停用失败：请检查系统安全管理权限，且不能停用当前账号。');
    } finally {
      setBusy(false);
    }
  }
  async function createEmployee() {
    if (busy || !username.trim() || !displayName.trim()) return;
    setBusy(true);
    setMessage('');
    try {
      await api.send(`/api/admin/m8/apps/${encodeURIComponent(appId)}/security/users`, 'POST', {
        username: username.trim(),
        displayName: displayName.trim(),
      });
      setUsername('');
      setDisplayName('');
      setMessage('员工账号已创建；密码须通过受控凭据配置流程开通。');
      state.reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '员工创建失败');
    } finally {
      setBusy(false);
    }
  }
  async function saveRoles() {
    if (!roleUser || busy) return;
    setBusy(true);
    setMessage('');
    try {
      await api.send(
        `/api/admin/m8/apps/${encodeURIComponent(appId)}/security/users/${encodeURIComponent(safe(roleUser.id))}/roles`,
        'PUT',
        { roleIds: selectedRoleIds },
      );
      setRoleUser(undefined);
      setMessage('员工角色已更新，其现有会话已注销。');
      state.reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '角色更新失败');
    } finally {
      setBusy(false);
    }
  }
  async function saveAssignments() {
    if (!assignmentUser || busy) return;
    setBusy(true);
    setMessage('');
    try {
      await api.send(
        `/api/admin/m8/apps/${encodeURIComponent(appId)}/security/users/${encodeURIComponent(safe(assignmentUser.id))}/assignments`,
        'PUT',
        {
          assignments: assignmentRows.map((item) => ({
            objectType: safe(item.objectType),
            objectId: safe(item.objectId),
            ...(typeof item.validUntil === 'string' ? { validUntil: item.validUntil } : {}),
          })),
        },
      );
      setAssignmentUser(undefined);
      setMessage('员工数据分配已更新。');
      state.reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '数据分配保存失败');
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Header title="系统用户管理" subtitle="系统与权限 / 用户与身份绑定" />
      <Notice>运营微信身份必须与后台员工账号完成授权绑定；访问运营工作台不会自动成为员工。</Notice>
      <Metrics
        items={[
          { label: '员工账号', value: users.length, note: '当前系统登记' },
          {
            label: '启用账号',
            value: users.filter((row) => row.status === 'ACTIVE').length,
            note: '可参与授权计算',
          },
          {
            label: '运营微信绑定',
            value: users.filter((row) =>
              arrayRecords(row, 'identities').some(
                (identity) => identity.provider === 'OPS_WECHAT',
              ),
            ).length,
            note: '仍受员工账号约束',
          },
          {
            label: '已停用',
            value: users.filter((row) => row.status === 'DISABLED').length,
            note: '会话同步失效',
          },
        ]}
      />
      {message && <Notice warning={message.startsWith('停用失败')}>{message}</Notice>}
      <Panel
        title="新建员工账号"
        className="operations-inline-panel"
        hint="创建账号不会自动授予角色或生产权限"
      >
        <div className="r5-form-grid">
          <label>
            登录账号
            <input value={username} onChange={(event) => setUsername(event.target.value)} />
          </label>
          <label>
            员工姓名
            <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
          </label>
        </div>
        <div className="r5-form-actions">
          <button
            className="primary"
            disabled={busy || !username.trim() || !displayName.trim()}
            onClick={() => void createEmployee()}
          >
            {busy ? '创建中…' : '创建员工'}
          </button>
        </div>
      </Panel>
      <Panel title="员工账号与授权绑定">
        <State state={state} count={users.length} noun="系统用户">
          <div className="r5-table-scroll">
            <table className="r4-table">
              <thead>
                <tr>
                  <th>员工</th>
                  <th>登录账号</th>
                  <th>角色</th>
                  <th>身份绑定</th>
                  <th>任务分配</th>
                  <th>状态</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {users.map((row) => (
                  <tr key={safe(row.id)}>
                    <td>
                      <strong>{userName(row)}</strong>
                      <small>会话版本 {Number(row.version ?? 0)}</small>
                    </td>
                    <td>{accountName(row.username)}</td>
                    <td>
                      <div className="r4-tags">
                        {roleRefs(row).map((ref) => {
                          const role = nested(ref, 'role');
                          return <em key={safe(role.id)}>{roleName(role)}</em>;
                        })}
                      </div>
                    </td>
                    <td>
                      {arrayRecords(row, 'identities')
                        .map((identity) => identityLabels[safe(identity.provider)] ?? '受控身份')
                        .join('、') || '尚未绑定'}
                    </td>
                    <td>{countOf(row, 'assignments')} 项</td>
                    <td>
                      <Pill value={row.status} />
                    </td>
                    <td>
                      <button
                        className="link-button"
                        onClick={() => {
                          setRoleUser(row);
                          setSelectedRoleIds(
                            roleRefs(row).map((ref) => safe(nested(ref, 'role').id)),
                          );
                        }}
                      >
                        配置角色
                      </button>
                      <button
                        className="link-button"
                        onClick={() => {
                          setAssignmentUser(row);
                          setAssignmentRows(arrayRecords(row, 'assignments'));
                        }}
                      >
                        数据分配
                      </button>
                      {row.status === 'DISABLED' && (
                        <button
                          className="link-button"
                          disabled={busy}
                          onClick={() => {
                            setBusy(true);
                            void api
                              .send(
                                `/api/admin/m8/apps/${encodeURIComponent(appId)}/security/users/${encodeURIComponent(safe(row.id))}/enable`,
                                'POST',
                                { expectedVersion: Number(row.version ?? 0) },
                              )
                              .then(() => {
                                setMessage('员工账号已恢复，权限仍按当前角色计算。');
                                state.reload();
                              })
                              .catch((error: unknown) =>
                                setMessage(error instanceof Error ? error.message : '恢复失败'),
                              )
                              .finally(() => setBusy(false));
                          }}
                        >
                          恢复
                        </button>
                      )}
                      <button
                        className="link-button"
                        disabled={row.status !== 'ACTIVE'}
                        onClick={() => setPending(row)}
                      >
                        停用
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
      {pending && (
        <Panel title="确认停用员工" hint="高风险权限变更将写入审计日志">
          <p>
            停用“{userName(pending)}
            ”后，其所有有效会话会立即失效，历史业务与审计记录不会删除。
          </p>
          <div className="r5-form-actions">
            <button onClick={() => setPending(undefined)}>取消</button>
            <button className="danger" disabled={busy} onClick={() => void disable()}>
              {busy ? '处理中…' : '确认停用并注销会话'}
            </button>
          </div>
        </Panel>
      )}
      {roleUser && (
        <Panel title={`配置“${userName(roleUser)}”的角色`} hint="保存后会注销该员工现有会话">
          <div className="r4-tags">
            {roles.map((role) => {
              const roleId = safe(role.id);
              return (
                <label key={roleId}>
                  <input
                    type="checkbox"
                    checked={selectedRoleIds.includes(roleId)}
                    onChange={(event) =>
                      setSelectedRoleIds((current) =>
                        event.target.checked
                          ? [...new Set([...current, roleId])]
                          : current.filter((id) => id !== roleId),
                      )
                    }
                  />
                  {roleName(role)}
                </label>
              );
            })}
          </div>
          <div className="r5-form-actions">
            <button onClick={() => setRoleUser(undefined)}>取消</button>
            <button className="primary" disabled={busy} onClick={() => void saveRoles()}>
              {busy ? '保存中…' : '保存角色'}
            </button>
          </div>
        </Panel>
      )}
      {assignmentUser && (
        <Panel
          title={`配置“${userName(assignmentUser)}”的数据分配`}
          hint="用于 ASSIGNED_ORDER 及受控对象范围"
        >
          <div className="r7-permission-list">
            {assignmentRows.map((item, index) => (
              <article key={`${safe(item.objectType)}-${safe(item.objectId)}-${index}`}>
                <span>
                  <strong>{safe(item.objectType)}</strong>
                  <small>{safe(item.objectId)}</small>
                </span>
                <button
                  onClick={() =>
                    setAssignmentRows((current) =>
                      current.filter((_, rowIndex) => rowIndex !== index),
                    )
                  }
                >
                  移除
                </button>
              </article>
            ))}
          </div>
          <div className="r5-form-grid">
            <label>
              对象类型
              <select
                value={assignmentType}
                onChange={(event) => setAssignmentType(event.target.value)}
              >
                {['APP', 'CITY', 'PRODUCT', 'SKU', 'CHANNEL', 'ORG', 'ASSIGNED_ORDER'].map(
                  (value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ),
                )}
              </select>
            </label>
            <label>
              对象 ID
              <input
                value={assignmentId}
                onChange={(event) => setAssignmentId(event.target.value)}
              />
            </label>
          </div>
          <div className="r5-form-actions">
            <button
              disabled={!assignmentId.trim()}
              onClick={() => {
                setAssignmentRows((current) => [
                  ...current.filter(
                    (item) =>
                      !(
                        safe(item.objectType) === assignmentType &&
                        safe(item.objectId) === assignmentId.trim()
                      ),
                  ),
                  { objectType: assignmentType, objectId: assignmentId.trim() },
                ]);
                setAssignmentId('');
              }}
            >
              加入分配
            </button>
            <button onClick={() => setAssignmentUser(undefined)}>取消</button>
            <button className="primary" disabled={busy} onClick={() => void saveAssignments()}>
              {busy ? '保存中…' : '保存分配'}
            </button>
          </div>
        </Panel>
      )}
    </>
  );
}

function RoleManagement({
  api,
  appId,
  state,
}: {
  api: AdminApiClient;
  appId: string;
  state: LoadState;
}) {
  const snapshot = unwrapRecord(state.data);
  const roles = unwrapList(snapshot.roles);
  const [selectedId, setSelectedId] = useState('');
  const [roleCodeValue, setRoleCodeValue] = useState('');
  const [roleNameValue, setRoleNameValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const selected = roles.find((row) => safe(row.id) === selectedId) ?? roles[0];
  const permissions = selected ? permissionRefs(selected) : [];
  return (
    <>
      <Header title="角色管理" subtitle="系统与权限 / 角色授权" />
      <Notice>同一员工可拥有多个角色；同类允许范围取并集，但任一显式拒绝都会覆盖允许。</Notice>
      {message && <Notice>{message}</Notice>}
      <Panel title="新建角色" className="operations-inline-panel" hint="新角色默认无任何权限">
        <div className="r5-form-grid">
          <label>
            角色编码
            <input
              value={roleCodeValue}
              placeholder="OPS_MANAGER"
              onChange={(event) => setRoleCodeValue(event.target.value.toUpperCase())}
            />
          </label>
          <label>
            角色名称
            <input
              value={roleNameValue}
              onChange={(event) => setRoleNameValue(event.target.value)}
            />
          </label>
        </div>
        <div className="r5-form-actions">
          <button
            className="primary"
            disabled={busy || !roleCodeValue.trim() || !roleNameValue.trim()}
            onClick={() => {
              setBusy(true);
              setMessage('');
              void api
                .send(`/api/admin/m8/apps/${encodeURIComponent(appId)}/security/roles`, 'POST', {
                  code: roleCodeValue.trim(),
                  name: roleNameValue.trim(),
                })
                .then(() => {
                  setRoleCodeValue('');
                  setRoleNameValue('');
                  setMessage('角色已创建，请继续配置功能、数据、字段与状态策略。');
                  state.reload();
                })
                .catch((error: unknown) =>
                  setMessage(error instanceof Error ? error.message : '角色创建失败'),
                )
                .finally(() => setBusy(false));
            }}
          >
            {busy ? '创建中…' : '创建角色'}
          </button>
        </div>
      </Panel>
      <div className="r7-role-layout">
        <Panel title="角色列表" hint={`${roles.length} 个角色`}>
          <State state={state} count={roles.length} noun="角色">
            <div className="r7-role-list">
              {roles.map((row) => (
                <button
                  key={safe(row.id)}
                  className={safe(row.id) === safe(selected?.id) ? 'active' : ''}
                  onClick={() => setSelectedId(safe(row.id))}
                >
                  <span>
                    <strong>{roleName(row)}</strong>
                    <small>{roleCode(row)}</small>
                  </span>
                  <b>{permissionRefs(row).length}</b>
                </button>
              ))}
            </div>
          </State>
        </Panel>
        <Panel title="角色授权详情" hint="服务端实时计算">
          {!selected ? (
            <div className="r4-state">请选择角色。</div>
          ) : (
            <>
              <div className="r7-role-hero">
                <div>
                  <h3>{roleName(selected)}</h3>
                  <p>
                    {/(?:DEV[/-]?TEST|fixture|synthetic)/iu.test(safe(selected.description, ''))
                      ? '仅用于本地开发与自动化测试，不代表生产员工角色。'
                      : safe(selected.description, '暂无角色说明')}
                  </p>
                </div>
                <Pill value={selected.status} />
              </div>
              <Metrics
                items={[
                  { label: '功能权限', value: permissions.length, note: '拒绝优先于允许' },
                  {
                    label: '数据范围',
                    value: countOf(selected, 'dataScopes'),
                    note: '按授权对象隔离',
                  },
                  {
                    label: '字段策略',
                    value: countOf(selected, 'fieldPolicies'),
                    note: '服务端裁剪',
                  },
                  {
                    label: '已绑定员工',
                    value: countOf(selected, 'users'),
                    note: '多角色允许叠加',
                  },
                ]}
              />
              <div className="r7-permission-list">
                {permissions.map((ref) => {
                  const permission = nested(ref, 'permission');
                  return (
                    <article key={safe(permission.id)}>
                      <span>
                        <strong>{permissionName(permission)}</strong>
                        <small>{safe(permission.code, '受控权限')}</small>
                      </span>
                      <Pill value={ref.effect} />
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </Panel>
      </div>
    </>
  );
}

function PermissionMatrix({
  api,
  appId,
  state,
}: {
  api: AdminApiClient;
  appId: string;
  state: LoadState;
}) {
  const snapshot = unwrapRecord(state.data);
  const roles = unwrapList(snapshot.roles);
  const permissions = unwrapList(snapshot.permissions);
  const [roleId, setRoleId] = useState('');
  const [permissionId, setPermissionId] = useState('');
  const [effect, setEffect] = useState<'ALLOW' | 'DENY' | 'REMOVE'>('ALLOW');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  async function savePermission() {
    const role = roles.find((item) => safe(item.id) === roleId);
    if (!role || !permissionId || busy) return;
    const existing = permissionRefs(role)
      .filter((ref) => safe(nested(ref, 'permission').id) !== permissionId)
      .map((ref) => ({
        permissionId: safe(nested(ref, 'permission').id),
        effect: ref.effect === 'DENY' ? 'DENY' : 'ALLOW',
      }));
    const rules = effect === 'REMOVE' ? existing : [...existing, { permissionId, effect }];
    setBusy(true);
    setMessage('');
    try {
      await api.send(
        `/api/admin/m8/apps/${encodeURIComponent(appId)}/security/roles/${encodeURIComponent(roleId)}/permissions`,
        'PUT',
        { rules },
      );
      setMessage('功能权限已更新并写入审计日志。');
      state.reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '功能权限更新失败');
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Header title="功能权限矩阵" subtitle="系统与权限 / 功能权限" />
      <Notice>
        最终权限 = 功能权限 × 数据范围 × 字段策略 × 业务状态；前端按钮隐藏不能替代服务端授权。
      </Notice>
      <Metrics
        items={[
          { label: '功能权限', value: permissions.length, note: '默认拒绝' },
          { label: '角色', value: roles.length, note: '多角色联合计算' },
          {
            label: '高风险权限',
            value: permissions.filter((row) => row.highRisk === true).length,
            note: '单独审计',
          },
          {
            label: '停用权限',
            value: permissions.filter((row) => row.status === 'DISABLED').length,
            note: '不参与有效权限',
          },
        ]}
      />
      {message && <Notice>{message}</Notice>}
      <Panel title="调整角色功能权限" hint="DENY 优先于所有 ALLOW">
        <div className="r5-form-grid" id="permission-editor">
          <label>
            角色
            <select value={roleId} onChange={(event) => setRoleId(event.target.value)}>
              <option value="">请选择</option>
              {roles.map((role) => (
                <option key={safe(role.id)} value={safe(role.id)}>
                  {roleName(role)}
                </option>
              ))}
            </select>
          </label>
          <label>
            功能权限
            <select value={permissionId} onChange={(event) => setPermissionId(event.target.value)}>
              <option value="">请选择</option>
              {permissions.map((permission) => (
                <option key={safe(permission.id)} value={safe(permission.id)}>
                  {permissionName(permission)} · {safe(permission.code)}
                </option>
              ))}
            </select>
          </label>
          <label>
            效果
            <select
              value={effect}
              onChange={(event) => setEffect(event.target.value as typeof effect)}
            >
              <option value="ALLOW">允许</option>
              <option value="DENY">显式拒绝</option>
              <option value="REMOVE">移除规则（默认拒绝）</option>
            </select>
          </label>
        </div>
        <div className="r5-form-actions">
          <button
            className="primary"
            disabled={busy || !roleId || !permissionId}
            onClick={() => void savePermission()}
          >
            {busy ? '保存中…' : '保存权限'}
          </button>
        </div>
      </Panel>
      <Panel title="角色与功能权限">
        <State state={state} count={permissions.length} noun="功能权限">
          <div className="r5-table-scroll">
            <table className="r4-table r7-matrix">
              <thead>
                <tr>
                  <th>业务功能</th>
                  <th>权限编码</th>
                  {roles.map((role) => (
                    <th key={safe(role.id)}>{roleName(role)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {permissions.map((permission) => (
                  <tr key={safe(permission.id)}>
                    <td>
                      <strong>{permissionName(permission)}</strong>
                      {permission.highRisk === true && <small className="r6-danger">高风险</small>}
                    </td>
                    <td>
                      <code>{safe(permission.code, '—')}</code>
                    </td>
                    {roles.map((role) => {
                      const ref = permissionRefs(role).find(
                        (item) => safe(nested(item, 'permission').id) === safe(permission.id),
                      );
                      return (
                        <td key={safe(role.id)}>
                          <button
                            className="operations-matrix-action"
                            aria-label={`调整${roleName(role)}的${permissionName(permission)}`}
                            onClick={() => {
                              setRoleId(safe(role.id));
                              setPermissionId(safe(permission.id));
                              setEffect(ref?.effect === 'DENY' ? 'DENY' : ref ? 'ALLOW' : 'REMOVE');
                              document
                                .getElementById('permission-editor')
                                ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                            }}
                          >
                            {ref ? (
                              <Pill value={ref.effect} />
                            ) : (
                              <span className="r7-denied">默认拒绝</span>
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
    </>
  );
}

function DataScopes({
  api,
  appId,
  state,
  appsState,
  citiesState,
}: {
  api: AdminApiClient;
  appId: string;
  state: LoadState;
  appsState: LoadState;
  citiesState: LoadState;
}) {
  const snapshot = unwrapRecord(state.data);
  const rows = unwrapList(snapshot.dataScopes);
  const roles = mapById(unwrapList(snapshot.roles));
  const permissions = mapById(unwrapList(snapshot.permissions));
  const apps = mapById(unwrapList(appsState.data));
  const cities = mapById(unwrapList(citiesState.data));
  const roleRows = unwrapList(snapshot.roles);
  const permissionRows = unwrapList(snapshot.permissions);
  const [roleId, setRoleId] = useState('');
  const [permissionId, setPermissionId] = useState('');
  const [scopeType, setScopeType] = useState('APP');
  const [scopeValue, setScopeValue] = useState('');
  const [editingScopeId, setEditingScopeId] = useState('');
  const [effect, setEffect] = useState('ALLOW');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const scopeUnavailable =
    busy ||
    state.loading ||
    Boolean(state.error) ||
    (scopeType === 'APP' && (appsState.loading || Boolean(appsState.error))) ||
    (scopeType === 'CITY' && (citiesState.loading || Boolean(citiesState.error)));
  async function saveScope() {
    if (!roleId || !permissionId || !scopeValue.trim() || scopeUnavailable) return;
    const existing = rows
      .filter((item) => safe(item.roleId) === roleId)
      .filter((item) => !editingScopeId || safe(item.id, '') !== editingScopeId)
      .filter(
        (item) =>
          !(
            safe(item.permissionId) === permissionId &&
            safe(item.scopeType) === scopeType &&
            safe(item.scopeValue) === scopeValue.trim()
          ),
      )
      .map((item) => ({
        permissionId: safe(item.permissionId),
        scopeType: safe(item.scopeType),
        scopeValue: safe(item.scopeValue),
        effect: safe(item.effect),
      }));
    setBusy(true);
    setMessage('');
    try {
      await api.send(
        `/api/admin/m8/apps/${encodeURIComponent(appId)}/security/roles/${encodeURIComponent(roleId)}/data-scopes`,
        'PUT',
        {
          rules: [...existing, { permissionId, scopeType, scopeValue: scopeValue.trim(), effect }],
        },
      );
      setMessage('数据范围规则已保存。');
      setEditingScopeId('');
      state.reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '数据范围保存失败');
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Header title="数据范围配置" subtitle="系统与权限 / 数据范围" />
      <Notice warning>
        数据范围以服务端授权对象为准；修改请求参数不能扩大权限，显式拒绝始终覆盖允许。
      </Notice>
      <Metrics
        items={[
          { label: '范围规则', value: rows.length, note: '当前有效配置' },
          {
            label: '允许规则',
            value: rows.filter((row) => row.effect === 'ALLOW').length,
            note: '同类范围取并集',
          },
          {
            label: '拒绝规则',
            value: rows.filter((row) => row.effect === 'DENY').length,
            note: '优先级最高',
          },
          {
            label: '范围类型',
            value: new Set(rows.map((row) => safe(row.scopeType))).size,
            note: '按业务对象隔离',
          },
        ]}
      />
      {message && <Notice>{message}</Notice>}
      <Panel
        title="配置数据范围"
        hint={editingScopeId ? '编辑已有规则，保存后替换原规则' : '保存后立即参与服务端授权计算'}
      >
        <div className="r5-form-grid" id="data-scope-editor">
          <label>
            角色
            <select
              value={roleId}
              disabled={Boolean(editingScopeId) || scopeUnavailable}
              onChange={(event) => setRoleId(event.target.value)}
            >
              <option value="">请选择</option>
              {roleRows.map((role) => (
                <option key={safe(role.id)} value={safe(role.id)}>
                  {roleName(role)}
                </option>
              ))}
            </select>
          </label>
          <label>
            功能
            <select value={permissionId} onChange={(event) => setPermissionId(event.target.value)}>
              <option value="">请选择</option>
              {permissionRows.map((permission) => (
                <option key={safe(permission.id)} value={safe(permission.id)}>
                  {permissionName(permission)}
                </option>
              ))}
            </select>
          </label>
          <label>
            范围类型
            <select
              value={scopeType}
              onChange={(event) => {
                setScopeType(event.target.value);
                setScopeValue(event.target.value === 'ALL' ? '*' : '');
              }}
            >
              {[
                'ALL',
                'APP',
                'CITY',
                'PRODUCT',
                'SKU',
                'CHANNEL',
                'ORG',
                'ASSIGNED_ORDER',
                'SELF',
              ].map((value) => (
                <option key={value} value={value}>
                  {scopeLabels[value] ?? value}
                </option>
              ))}
            </select>
          </label>
          {scopeType !== 'ALL' && (
            <label>
              授权对象
              {['APP', 'CITY'].includes(scopeType) ? (
                <select
                  value={scopeValue}
                  disabled={
                    scopeType === 'APP'
                      ? appsState.loading || Boolean(appsState.error)
                      : citiesState.loading || Boolean(citiesState.error)
                  }
                  onChange={(event) => setScopeValue(event.target.value)}
                >
                  <option value="">{scopeType === 'APP' ? '请选择小程序' : '请选择城市'}</option>
                  {Array.from((scopeType === 'APP' ? apps : cities).values()).map((row) => (
                    <option key={safe(row.id)} value={safe(row.id)}>
                      {scopeType === 'APP'
                        ? businessName(row.displayName, safe(row.appKey, '未命名'))
                        : businessName(row.name, safe(row.code, '未命名'))}
                    </option>
                  ))}
                  {scopeValue && !(scopeType === 'APP' ? apps : cities).has(scopeValue) && (
                    <option value={scopeValue}>已配置对象（{scopeValue}）</option>
                  )}
                </select>
              ) : (
                <input
                  value={scopeValue}
                  placeholder="填写已授权业务对象 ID"
                  onChange={(event) => setScopeValue(event.target.value)}
                />
              )}
              {(scopeType === 'APP' && appsState.error) ||
              (scopeType === 'CITY' && citiesState.error) ? (
                <small role="alert">授权对象读取失败，请刷新后重试。</small>
              ) : null}
            </label>
          )}
          <label>
            效果
            <select value={effect} onChange={(event) => setEffect(event.target.value)}>
              <option value="ALLOW">允许</option>
              <option value="DENY">拒绝</option>
            </select>
          </label>
        </div>
        <div className="r5-form-actions">
          <button
            className="primary"
            disabled={scopeUnavailable || !roleId || !permissionId || !scopeValue.trim()}
            onClick={() => void saveScope()}
          >
            {busy ? '保存中…' : '保存范围'}
          </button>
          {editingScopeId && (
            <button
              disabled={busy}
              onClick={() => {
                setEditingScopeId('');
                setRoleId('');
                setPermissionId('');
                setScopeType('APP');
                setScopeValue('');
                setEffect('ALLOW');
              }}
            >
              取消编辑
            </button>
          )}
        </div>
      </Panel>
      <Panel title="范围授权规则">
        <State state={state} count={rows.length} noun="数据范围规则">
          <div className="r5-table-scroll">
            <table className="r4-table">
              <thead>
                <tr>
                  <th>角色</th>
                  <th>功能</th>
                  <th>范围类型</th>
                  <th>授权对象</th>
                  <th>效果</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const role = roles.get(safe(row.roleId)) ?? {};
                  const permission = permissions.get(safe(row.permissionId)) ?? {};
                  return (
                    <tr key={safe(row.id)}>
                      <td>{roleName(role)}</td>
                      <td>
                        <strong>{permissionName(permission)}</strong>
                        <small>{safe(permission.code, '受控权限')}</small>
                      </td>
                      <td>{scopeLabels[safe(row.scopeType)] ?? '受控范围'}</td>
                      <td>{valueName(row, apps, cities)}</td>
                      <td>
                        <Pill value={row.effect} />
                      </td>
                      <td>
                        <button
                          className="r5-link-button"
                          onClick={() => {
                            setRoleId(safe(row.roleId));
                            setEditingScopeId(safe(row.id));
                            setPermissionId(safe(row.permissionId));
                            setScopeType(safe(row.scopeType));
                            setScopeValue(safe(row.scopeValue));
                            setEffect(safe(row.effect));
                            document
                              .getElementById('data-scope-editor')
                              ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                          }}
                        >
                          编辑
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
      <p className="operations-rule-note">
        未授权即拒绝，同类范围允许取并集，显式拒绝优先。数据访问同时校验功能权限、字段策略和业务状态。
      </p>
    </>
  );
}

function FieldPolicies({
  api,
  appId,
  state,
}: {
  api: AdminApiClient;
  appId: string;
  state: LoadState;
}) {
  const snapshot = unwrapRecord(state.data);
  const rows = unwrapList(snapshot.fieldPolicies);
  const roles = mapById(unwrapList(snapshot.roles));
  const permissions = mapById(unwrapList(snapshot.permissions));
  const levels = ['L0', 'L1', 'L2', 'L3'];
  const roleRows = unwrapList(snapshot.roles);
  const permissionRows = unwrapList(snapshot.permissions);
  const [roleId, setRoleId] = useState('');
  const [permissionId, setPermissionId] = useState('');
  const [fieldName, setFieldName] = useState('');
  const [editingFieldId, setEditingFieldId] = useState('');
  const [classification, setClassification] = useState('L1');
  const [access, setAccess] = useState('MASK');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const fieldUnavailable = busy || state.loading || Boolean(state.error);
  async function saveFieldPolicy() {
    if (
      !roleId ||
      !permissionId ||
      !fieldName.trim() ||
      fieldUnavailable ||
      (classification === 'L3' && access === 'ALLOW')
    )
      return;
    const existing = rows
      .filter((item) => safe(item.roleId) === roleId)
      .filter((item) => !editingFieldId || safe(item.id, '') !== editingFieldId)
      .filter(
        (item) =>
          !(safe(item.permissionId) === permissionId && safe(item.fieldName) === fieldName.trim()),
      )
      .map((item) => ({
        permissionId: safe(item.permissionId),
        fieldName: safe(item.fieldName),
        classification: safe(item.classification),
        access: safe(item.access),
      }));
    setBusy(true);
    setMessage('');
    try {
      await api.send(
        `/api/admin/m8/apps/${encodeURIComponent(appId)}/security/roles/${encodeURIComponent(roleId)}/field-policies`,
        'PUT',
        {
          rules: [
            ...existing,
            { permissionId, fieldName: fieldName.trim(), classification, access },
          ],
        },
      );
      setMessage('字段策略已保存，服务端将按新规则裁剪。');
      setEditingFieldId('');
      state.reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '字段策略保存失败');
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Header title="字段权限配置" subtitle="系统与权限 / 字段安全" />
      {message && <Notice>{message}</Notice>}
      <Panel
        title="配置字段策略"
        hint={
          editingFieldId
            ? '编辑已有规则，保存后替换原规则'
            : '联系方式默认脱敏；支付密钥禁止明文访问'
        }
      >
        <div className="r5-form-grid" id="field-policy-editor">
          <label>
            角色
            <select
              value={roleId}
              disabled={Boolean(editingFieldId) || fieldUnavailable}
              onChange={(event) => setRoleId(event.target.value)}
            >
              <option value="">请选择</option>
              {roleRows.map((role) => (
                <option key={safe(role.id)} value={safe(role.id)}>
                  {roleName(role)}
                </option>
              ))}
            </select>
          </label>
          <label>
            功能
            <select value={permissionId} onChange={(event) => setPermissionId(event.target.value)}>
              <option value="">请选择</option>
              {permissionRows.map((permission) => (
                <option key={safe(permission.id)} value={safe(permission.id)}>
                  {permissionName(permission)}
                </option>
              ))}
            </select>
          </label>
          <label>
            字段路径
            <input
              value={fieldName}
              list="field-policy-suggestions"
              placeholder="contact.mobile"
              onChange={(event) => setFieldName(event.target.value)}
            />
            <datalist id="field-policy-suggestions">
              {Object.entries(fieldLabels).map(([value, text]) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </datalist>
          </label>
          <label>
            等级
            <select
              value={classification}
              onChange={(event) => {
                setClassification(event.target.value);
                if (event.target.value === 'L3') setAccess('DENY');
              }}
            >
              {levels.map((level, index) => (
                <option key={level} value={level}>
                  {level} ·{' '}
                  {['普通运营字段', '联系方式', '财务与退款字段', '支付密钥与证书'][index]}
                </option>
              ))}
            </select>
          </label>
          <label>
            访问策略
            <select value={access} onChange={(event) => setAccess(event.target.value)}>
              <option value="ALLOW" disabled={classification === 'L3'}>
                允许
              </option>
              <option value="MASK">脱敏</option>
              <option value="DENY">拒绝</option>
            </select>
          </label>
        </div>
        <div className="r5-form-actions">
          <button
            className="primary"
            disabled={
              fieldUnavailable ||
              !roleId ||
              !permissionId ||
              !fieldName.trim() ||
              (classification === 'L3' && access === 'ALLOW')
            }
            onClick={() => void saveFieldPolicy()}
          >
            {busy ? '保存中…' : '保存字段策略'}
          </button>
          {editingFieldId && (
            <button
              disabled={busy}
              onClick={() => {
                setEditingFieldId('');
                setRoleId('');
                setPermissionId('');
                setFieldName('');
                setClassification('L1');
                setAccess('MASK');
              }}
            >
              取消编辑
            </button>
          )}
        </div>
      </Panel>
      <Panel title="字段访问策略">
        <State state={state} count={rows.length} noun="字段策略">
          <div className="r5-table-scroll">
            <table className="r4-table">
              <thead>
                <tr>
                  <th>角色</th>
                  <th>业务功能</th>
                  <th>字段</th>
                  <th>等级</th>
                  <th>访问策略</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const role = roles.get(safe(row.roleId)) ?? {};
                  const permission = permissions.get(safe(row.permissionId)) ?? {};
                  return (
                    <tr key={safe(row.id)}>
                      <td>{roleName(role)}</td>
                      <td>{permissionName(permission)}</td>
                      <td>{fieldLabels[safe(row.fieldName)] ?? '受控字段'}</td>
                      <td>
                        <span className={`r7-level ${safe(row.classification).toLowerCase()}`}>
                          {safe(row.classification, 'L0')}
                        </span>
                      </td>
                      <td>
                        <Pill value={row.access} />
                      </td>
                      <td>
                        <button
                          className="r5-link-button"
                          onClick={() => {
                            setRoleId(safe(row.roleId));
                            setPermissionId(safe(row.permissionId));
                            setEditingFieldId(safe(row.id));
                            setFieldName(safe(row.fieldName));
                            setClassification(safe(row.classification));
                            setAccess(safe(row.access));
                            document
                              .getElementById('field-policy-editor')
                              ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                          }}
                        >
                          编辑
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
    </>
  );
}

function HighRisk({ api, appId, state }: { api: AdminApiClient; appId: string; state: LoadState }) {
  const snapshot = unwrapRecord(state.data);
  const permissions = unwrapList(snapshot.permissions).filter((row) => row.highRisk === true);
  const roles = unwrapList(snapshot.roles);
  const auditLogs = unwrapList(snapshot.auditLogs);
  const statePolicies = unwrapList(snapshot.statePolicies);
  const [roleId, setRoleId] = useState('');
  const [permissionId, setPermissionId] = useState('');
  const [businessState, setBusinessState] = useState('');
  const [effect, setEffect] = useState('ALLOW');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const statePolicyUnavailable = busy || state.loading || Boolean(state.error);
  async function saveStatePolicy() {
    if (!roleId || !permissionId || !businessState.trim() || statePolicyUnavailable) return;
    const existing = statePolicies
      .filter((item) => safe(item.roleId) === roleId)
      .filter(
        (item) =>
          !(safe(item.permissionId) === permissionId && safe(item.state) === businessState.trim()),
      )
      .map((item) => ({
        permissionId: safe(item.permissionId),
        state: safe(item.state),
        effect: safe(item.effect),
      }));
    setBusy(true);
    setMessage('');
    try {
      await api.send(
        `/api/admin/m8/apps/${encodeURIComponent(appId)}/security/roles/${encodeURIComponent(roleId)}/state-policies`,
        'PUT',
        { rules: [...existing, { permissionId, state: businessState.trim(), effect }] },
      );
      setMessage('业务状态权限已保存，将与功能、范围和字段策略独立计算。');
      state.reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '状态权限保存失败');
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Header title="高风险动作授权" subtitle="系统与权限 / 高风险操作" />
      <Notice warning>
        退款、敏感导出、权限变更、商户变更、正式发布与结算必须独立授权并强制审计；当前页面不授予临时生产权限。
      </Notice>
      <Metrics
        items={[
          { label: '高风险权限', value: permissions.length, note: '独立计算' },
          {
            label: '已绑定角色',
            value: roles.filter((role) =>
              permissionRefs(role).some((ref) => nested(ref, 'permission').highRisk === true),
            ).length,
            note: '仍受范围和字段约束',
          },
          {
            label: '近期审计',
            value: auditLogs.filter((row) =>
              [
                'refund',
                'export',
                'permission.change',
                'merchant.change',
                'release',
                'settlement',
              ].includes(safe(row.action)),
            ).length,
            note: '强制留痕',
          },
          { label: '临时授权', value: 0, note: '本版本未开放自助授予' },
        ]}
      />
      {message && <Notice>{message}</Notice>}
      <Panel title="配置业务状态权限" hint="高风险命令在当前业务状态下单独判定">
        <div className="r5-form-grid">
          <label>
            角色
            <select value={roleId} onChange={(event) => setRoleId(event.target.value)}>
              <option value="">请选择</option>
              {roles.map((role) => (
                <option key={safe(role.id)} value={safe(role.id)}>
                  {roleName(role)}
                </option>
              ))}
            </select>
          </label>
          <label>
            高风险权限
            <select value={permissionId} onChange={(event) => setPermissionId(event.target.value)}>
              <option value="">请选择</option>
              {permissions.map((permission) => (
                <option key={safe(permission.id)} value={safe(permission.id)}>
                  {permissionName(permission)}
                </option>
              ))}
            </select>
          </label>
          <label>
            业务状态
            <input
              value={businessState}
              placeholder="CONFIRMED"
              onChange={(event) => setBusinessState(event.target.value.toUpperCase())}
            />
          </label>
          <label>
            效果
            <select value={effect} onChange={(event) => setEffect(event.target.value)}>
              <option value="ALLOW">允许</option>
              <option value="DENY">拒绝</option>
            </select>
          </label>
        </div>
        <div className="r5-form-actions">
          <button
            className="primary"
            disabled={statePolicyUnavailable || !roleId || !permissionId || !businessState.trim()}
            onClick={() => void saveStatePolicy()}
          >
            {busy ? '保存中…' : '保存状态权限'}
          </button>
        </div>
      </Panel>
      <Panel title="高风险动作目录" hint="授权变更需要独立审批能力">
        <State state={state} count={permissions.length} noun="高风险权限">
          <div className="r5-table-scroll">
            <table className="r4-table">
              <thead>
                <tr>
                  <th>高风险动作</th>
                  <th>权限编码</th>
                  <th>授权角色</th>
                  <th>必要保护</th>
                  <th>状态</th>
                </tr>
              </thead>
              <tbody>
                {permissions.map((permission) => {
                  const bound = roles.filter((role) =>
                    permissionRefs(role).some(
                      (ref) =>
                        safe(nested(ref, 'permission').id) === safe(permission.id) &&
                        ref.effect === 'ALLOW',
                    ),
                  );
                  return (
                    <tr key={safe(permission.id)}>
                      <td>
                        <strong>{permissionName(permission)}</strong>
                      </td>
                      <td>
                        <code>{safe(permission.code)}</code>
                      </td>
                      <td>{bound.map((role) => roleName(role)).join('、') || '未授权'}</td>
                      <td>服务端权限 + 业务范围 + 强制审计</td>
                      <td>
                        <Pill value={permission.status} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
    </>
  );
}

function SystemSettings({ state }: { state: LoadState }) {
  const auditCount = unwrapList(unwrapRecord(state.data).auditLogs).length;
  const settings = [
    ['小程序配置', '名称、服务城市、客服与收款商户', '/admin/apps'],
    ['员工与角色', '账号状态、角色及操作权限', '/admin/security/users'],
    ['数据范围', '分配小程序和城市的管理范围', '/admin/security/data-scopes'],
    ['支付配置', '商户绑定、收款路由和异常保护', '/admin/payments/merchants'],
    ['消息通知', '通知模板、发送任务及订阅记录', '/admin/messages/templates'],
    ['运营配置', '运营工作台、服务与执行规则', '/admin/ops/config'],
    ['发布中心', '版本、发布检查及发布记录', '/admin/releases'],
    [
      '操作日志',
      state.loading
        ? '正在读取记录…'
        : state.error
          ? '进入查看记录'
          : `当前可见 ${auditCount} 条记录`,
      '/admin/audit/overview',
    ],
  ];
  return (
    <>
      <Header title="系统设置" subtitle="选择配置事项，进入对应维护页面" />
      <Panel title="配置入口">
        <nav className="erp-settings-directory" aria-label="系统配置入口">
          {settings.map(([name, description, href]) => (
            <a key={href} href={href}>
              <strong>{name}</strong>
              <span>{description}</span>
              <b aria-hidden="true">›</b>
            </a>
          ))}
        </nav>
      </Panel>
    </>
  );
}
function releaseDisplay(row: JsonRecord) {
  return `${safe(row.version, '未命名版本')} · 构建 ${Number(row.buildNumber ?? 0)}`;
}

function ReleaseCenter({
  api,
  appId,
  state,
}: {
  api: AdminApiClient;
  appId: string;
  state: LoadState;
}) {
  const rows = unwrapList(state.data);
  const [showCreate, setShowCreate] = useState(false);
  const [version, setVersion] = useState('');
  const [buildNumber, setBuildNumber] = useState('');
  const [sourceCommit, setSourceCommit] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  async function create() {
    if (
      !version.trim() ||
      !/^\d+$/u.test(buildNumber) ||
      !/^[0-9a-f]{7,64}$/iu.test(sourceCommit) ||
      busy
    )
      return;
    setBusy(true);
    setMessage('');
    try {
      await api.send(`/api/admin/m8/apps/${encodeURIComponent(appId)}/releases`, 'POST', {
        version: version.trim(),
        buildNumber: Number(buildNumber),
        sourceCommit: sourceCommit.trim(),
        idempotencyKey: `admin:${version.trim()}:${buildNumber}`,
      });
      setShowCreate(false);
      setMessage('发布记录已创建，需继续完成内部门禁与外部微信平台验证。');
      state.reload();
    } catch {
      setMessage('创建失败，请检查版本、构建号、源码提交和发布权限。');
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Header
        title="多小程序构建发布中心"
        subtitle="发布与运维 / 小程序发布"
        actions={
          <button className="primary" onClick={() => setShowCreate((value) => !value)}>
            + 创建发布任务
          </button>
        }
      />
      <Notice warning>
        系统只自动完成配置生成、构建、测试和证据登记；微信上传、审核与正式发布仍受外部平台和人工授权约束。
      </Notice>
      <Metrics
        items={[
          { label: '发布记录', value: rows.length, note: '当前小程序' },
          {
            label: '构建完成',
            value: rows.filter((row) =>
              [
                'BUILT',
                'EXTERNAL_VALIDATION_PENDING',
                'UPLOADED',
                'UNDER_REVIEW',
                'APPROVED',
                'RELEASED',
              ].includes(safe(row.status)),
            ).length,
            note: '具备产物证明',
          },
          {
            label: '等待微信验证',
            value: rows.filter((row) => row.status === 'EXTERNAL_VALIDATION_PENDING').length,
            note: '不能视为发布通过',
          },
          {
            label: '已正式发布',
            value: rows.filter((row) => row.status === 'RELEASED').length,
            note: '需真实平台回执',
          },
        ]}
      />
      {message && <Notice warning={message.startsWith('创建失败')}>{message}</Notice>}
      {showCreate && (
        <Panel title="创建发布任务" hint="代码发布与 CMS 内容发布相互独立">
          <div className="r5-form-grid two">
            <label>
              版本号
              <input
                value={version}
                onChange={(event) => setVersion(event.target.value)}
                placeholder="例如 1.2.0"
              />
            </label>
            <label>
              构建号
              <input
                type="number"
                min="1"
                value={buildNumber}
                onChange={(event) => setBuildNumber(event.target.value)}
              />
            </label>
            <label>
              源码提交
              <input
                value={sourceCommit}
                onChange={(event) => setSourceCommit(event.target.value)}
                placeholder="7–64 位提交哈希"
              />
            </label>
          </div>
          <div className="r5-form-actions">
            <button onClick={() => setShowCreate(false)}>取消</button>
            <button
              className="primary"
              disabled={
                busy ||
                !version.trim() ||
                !/^\d+$/u.test(buildNumber) ||
                !/^[0-9a-f]{7,64}$/iu.test(sourceCommit)
              }
              onClick={() => void create()}
            >
              {busy ? '创建中…' : '创建并进入门禁'}
            </button>
          </div>
        </Panel>
      )}
      <Panel title="发布任务">
        <State state={state} count={rows.length} noun="发布记录">
          <div className="r5-table-scroll">
            <table className="r4-table">
              <thead>
                <tr>
                  <th>版本</th>
                  <th>源码提交</th>
                  <th>内部门禁</th>
                  <th>微信平台</th>
                  <th>当前状态</th>
                  <th>更新时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const gates = arrayRecords(row, 'gates');
                  const internal = gates.filter(
                    (gate) => safe(gate.gateCode) !== 'WECHAT_EXTERNAL',
                  );
                  const external = gates.find((gate) => safe(gate.gateCode) === 'WECHAT_EXTERNAL');
                  return (
                    <tr key={safe(row.id)}>
                      <td>
                        <strong>{releaseDisplay(row)}</strong>
                      </td>
                      <td>
                        <code>{safe(row.sourceCommit).slice(0, 10)}</code>
                      </td>
                      <td>
                        {internal.filter((gate) => gate.status === 'PASSED').length}/
                        {internal.length} 通过
                      </td>
                      <td>{label(external?.status, '待验证')}</td>
                      <td>
                        <Pill value={row.status} />
                      </td>
                      <td>{when(row.updatedAt, true)}</td>
                      <td>
                        <a href={pathFor('ADM-J09', { id: safe(row.id) })}>详情</a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
    </>
  );
}

function ReleaseGate({
  api,
  appId,
  state,
  id,
}: {
  api: AdminApiClient;
  appId: string;
  state: LoadState;
  id: string;
}) {
  const rows = unwrapList(state.data);
  const release = rows.find((row) => safe(row.id) === id);
  const gates = release ? arrayRecords(release, 'gates') : [];
  const steps = ['DRAFT', 'BUILD_REQUESTED', 'BUILT', 'UPLOADED', 'UNDER_REVIEW', 'RELEASED'];
  const current = release ? Math.max(0, steps.indexOf(safe(release.status))) : 0;
  const [evidenceNote, setEvidenceNote] = useState('');
  const [artifactRef, setArtifactRef] = useState('');
  const [artifactSha256, setArtifactSha256] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  async function command(path: string, body?: unknown) {
    if (!release || busy) return;
    setBusy(true);
    setMessage('');
    try {
      await api.send(
        `/api/admin/m8/apps/${encodeURIComponent(appId)}/releases/${encodeURIComponent(safe(release.id))}/${path}`,
        'POST',
        body,
      );
      setMessage('发布流程状态已更新。');
      state.reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '发布命令执行失败');
    } finally {
      setBusy(false);
    }
  }
  async function recordGate(gateCode: string) {
    if (!evidenceNote.trim()) {
      setMessage('必须填写可核验的门禁证据摘要。');
      return;
    }
    await command('gates', {
      gateCode,
      status: 'PASSED',
      evidence: { source: 'ADMIN_ATTESTATION', note: evidenceNote.trim() },
    });
  }
  return (
    <>
      <Header
        title="发布详情审核门禁"
        subtitle="发布与运维 / 发布审核"
        actions={
          <a className="r6-button" href={pathFor('ADM-J08')}>
            返回发布中心
          </a>
        }
      />
      <Notice warning>
        正式发布是高风险动作。内部测试通过不能替代真实微信平台上传、审核回执和人工发布授权。
      </Notice>
      {message && <Notice warning>{message}</Notice>}
      <State state={state} count={release ? 1 : 0} noun="发布详情">
        {release && (
          <>
            <Panel title={releaseDisplay(release)} hint={label(release.status)}>
              <div className="r7-release-steps">
                {steps.map((step, index) => (
                  <div key={step} className={index <= current ? 'done' : ''}>
                    <i>{index < current ? '✓' : index + 1}</i>
                    <span>
                      {label(
                        step,
                        ['创建', '等待构建', '构建完成', '微信上传', '微信审核', '正式发布'][index],
                      )}
                    </span>
                  </div>
                ))}
              </div>
              <div className="r7-release-meta">
                <div>
                  <span>源码提交</span>
                  <strong>{safe(release.sourceCommit).slice(0, 12)}</strong>
                </div>
                <div>
                  <span>产物校验</span>
                  <strong>{release.artifactSha256 ? '已登记并校验格式' : '尚未登记'}</strong>
                </div>
                <div>
                  <span>微信审核回执</span>
                  <strong>{release.externalReviewRef ? '已登记平台回执' : '尚未获得'}</strong>
                </div>
                <div>
                  <span>生产发布</span>
                  <strong>{release.status === 'RELEASED' ? '已完成' : '未授权'}</strong>
                </div>
              </div>
            </Panel>
            <div className="r7-scope-layout">
              <Panel title="发布门禁检查">
                <table className="r4-table">
                  <thead>
                    <tr>
                      <th>门禁</th>
                      <th>结果</th>
                      <th>证据</th>
                      <th>检查时间</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gates.map((gate) => (
                      <tr key={safe(gate.id)}>
                        <td>
                          {(
                            {
                              BASELINE: '不可变基线',
                              SECURITY: '安全检查',
                              TEST: '自动化测试',
                              BUILD: '构建与锁定',
                              WECHAT_EXTERNAL: '微信平台验证',
                            } as Record<string, string>
                          )[safe(gate.gateCode)] ?? '受控门禁'}
                        </td>
                        <td>
                          <Pill value={gate.status} />
                        </td>
                        <td>{isRecord(gate.evidence) ? '已由受控流程登记' : '等待证据'}</td>
                        <td>{when(gate.checkedAt, true)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <label>
                  门禁证据摘要
                  <textarea
                    value={evidenceNote}
                    placeholder="填写测试记录、CI 任务或审查证据引用"
                    onChange={(event) => setEvidenceNote(event.target.value)}
                  />
                </label>
                <div className="r5-form-actions">
                  {['BASELINE', 'SECURITY', 'TEST', 'BUILD'].map((gateCode) => (
                    <button
                      key={gateCode}
                      disabled={
                        busy ||
                        gates.some((gate) => gate.gateCode === gateCode && gate.status === 'PASSED')
                      }
                      onClick={() => void recordGate(gateCode)}
                    >
                      登记 {gateCode}
                    </button>
                  ))}
                </div>
              </Panel>
              <Panel title="审批与发布动作">
                <dl className="r6-kv">
                  <div>
                    <dt>内部门禁</dt>
                    <dd>
                      {
                        gates.filter(
                          (gate) =>
                            safe(gate.gateCode) !== 'WECHAT_EXTERNAL' && gate.status === 'PASSED',
                        ).length
                      }
                      /{gates.filter((gate) => safe(gate.gateCode) !== 'WECHAT_EXTERNAL').length}{' '}
                      通过
                    </dd>
                  </div>
                  <div>
                    <dt>微信平台验证</dt>
                    <dd>
                      {label(
                        gates.find((gate) => gate.gateCode === 'WECHAT_EXTERNAL')?.status,
                        '等待验证',
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>发布授权</dt>
                    <dd>需要人工明确授权</dd>
                  </div>
                </dl>
                <Notice warning>
                  {release.externalReviewRef
                    ? '已获得外部回执，但正式发布仍需人工授权。'
                    : '尚未获得真实微信平台回执，上传、审核和正式发布按钮保持禁用。'}
                </Notice>
                <div className="r5-form-actions">
                  {release.status === 'DRAFT' && (
                    <button
                      disabled={
                        busy ||
                        gates.filter(
                          (gate) =>
                            ['BASELINE', 'SECURITY', 'TEST', 'BUILD'].includes(
                              safe(gate.gateCode),
                            ) && gate.status === 'PASSED',
                        ).length < 4
                      }
                      onClick={() => void command('request-build')}
                    >
                      请求构建
                    </button>
                  )}
                  {release.status === 'BUILD_REQUESTED' && (
                    <div className="r5-form-grid">
                      <label>
                        产物引用
                        <input
                          value={artifactRef}
                          onChange={(event) => setArtifactRef(event.target.value)}
                        />
                      </label>
                      <label>
                        SHA-256
                        <input
                          value={artifactSha256}
                          onChange={(event) => setArtifactSha256(event.target.value)}
                        />
                      </label>
                      <button
                        disabled={
                          busy || !artifactRef.trim() || !/^[0-9a-f]{64}$/u.test(artifactSha256)
                        }
                        onClick={() =>
                          void command('attest-build', {
                            artifactRef: artifactRef.trim(),
                            artifactSha256,
                          })
                        }
                      >
                        登记构建产物
                      </button>
                    </div>
                  )}
                  {release.status === 'BUILT' && (
                    <button disabled={busy} onClick={() => void command('upload')}>
                      进入微信上传门禁
                    </button>
                  )}
                  {release.status === 'UPLOADED' && (
                    <button disabled={busy} onClick={() => void command('submit-review')}>
                      提交微信审核
                    </button>
                  )}
                  {release.status === 'UNDER_REVIEW' && (
                    <button disabled={busy} onClick={() => void command('refresh-review')}>
                      刷新审核状态
                    </button>
                  )}
                  <button
                    disabled={!release.externalReviewRef}
                    onClick={() =>
                      setMessage(
                        release.externalReviewRef
                          ? '外部回执已登记，可在发布详情中审计。'
                          : '暂无外部回执。',
                      )
                    }
                  >
                    查看外部审核
                  </button>
                  <span className="r6-release-boundary" title="正式生产发布必须取得人工明确授权">
                    正式发布 · 等待人工授权
                  </span>
                </div>
              </Panel>
            </div>
          </>
        )}
      </State>
    </>
  );
}

function Operations({
  api,
  appId,
  ops,
  recovery,
}: {
  api: AdminApiClient;
  appId: string;
  ops: LoadState;
  recovery: LoadState;
}) {
  const durable = unwrapRecord(unwrapRecord(ops.data).durable);
  const runtime = unwrapRecord(unwrapRecord(ops.data).runtime);
  const backups = unwrapList(unwrapRecord(recovery.data).backups);
  const restores = unwrapList(unwrapRecord(recovery.data).restoreDrills);
  const recoveryCount = backups.length + restores.length;
  const [backupRef, setBackupRef] = useState('');
  const [backupSha, setBackupSha] = useState('');
  const [restoreBackupId, setRestoreBackupId] = useState('');
  const [restoreTarget, setRestoreTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  async function recordBackup() {
    if (!backupRef.trim() || !/^[0-9a-f]{64}$/u.test(backupSha) || busy) return;
    setBusy(true);
    const completedAt = new Date();
    try {
      await api.send(`/api/admin/m8/apps/${encodeURIComponent(appId)}/recovery/backups`, 'POST', {
        backupType: 'FULL',
        objectRef: backupRef.trim(),
        checksumSha256: backupSha,
        rpoSeconds: 0,
        startedAt: new Date(completedAt.valueOf() - 1_000).toISOString(),
        completedAt: completedAt.toISOString(),
      });
      setBackupRef('');
      setBackupSha('');
      setMessage('备份校验证据已登记；本命令不会伪造或代替备份文件。');
      recovery.reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '备份证据登记失败');
    } finally {
      setBusy(false);
    }
  }
  async function recordRestore() {
    if (
      !restoreBackupId ||
      !/^travel_restore_drill_[0-9]{8}_[0-9]{6}$/u.test(restoreTarget) ||
      busy
    )
      return;
    setBusy(true);
    const completedAt = new Date();
    try {
      await api.send(`/api/admin/m8/apps/${encodeURIComponent(appId)}/recovery/restores`, 'POST', {
        backupRunId: restoreBackupId,
        isolatedTarget: restoreTarget,
        rtoSeconds: 0,
        startedAt: new Date(completedAt.valueOf() - 1_000).toISOString(),
        completedAt: completedAt.toISOString(),
        verificationEvidence: { source: 'ADMIN_ATTESTATION', isolated: true },
      });
      setRestoreTarget('');
      setMessage('隔离恢复验证证据已登记。');
      recovery.reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '恢复证据登记失败');
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Header title="日志备份监控配置" subtitle="发布与运维 / 系统运维" />
      <Notice>
        生产运维必须覆盖可观测性、链路关联、告警、备份与隔离恢复演练；页面不返回密钥、连接串或内部拓扑。
      </Notice>
      <Metrics
        items={[
          {
            label: '待处理可靠事件',
            value: Number(durable.outboxPending ?? 0),
            note: 'PostgreSQL 权威记录',
          },
          {
            label: '失败可靠事件',
            value: Number(durable.outboxFailed ?? 0),
            note: Number(durable.outboxFailed ?? 0) ? '需要立即处理' : '当前健康',
          },
          { label: '消息积压', value: Number(durable.messageBacklog ?? 0), note: '异步任务队列' },
          { label: '未关闭告警', value: Number(durable.openAlerts ?? 0), note: '含已确认未恢复' },
          { label: '备份证据', value: backups.length, note: '只统计持久化记录' },
          { label: '恢复演练', value: restores.length, note: '隔离目标验证' },
        ]}
      />
      {message && <Notice>{message}</Notice>}
      <div className="r7-scope-layout">
        <Panel title="登记已完成备份证据" hint="仅接受已存在产物的引用与 SHA-256">
          <label>
            备份产物引用
            <input value={backupRef} onChange={(event) => setBackupRef(event.target.value)} />
          </label>
          <label>
            SHA-256
            <input
              value={backupSha}
              onChange={(event) => setBackupSha(event.target.value.toLowerCase())}
            />
          </label>
          <button
            className="primary"
            disabled={busy || !backupRef.trim() || !/^[0-9a-f]{64}$/u.test(backupSha)}
            onClick={() => void recordBackup()}
          >
            登记备份证据
          </button>
        </Panel>
        <Panel title="登记隔离恢复演练" hint="目标必须符合隔离数据库命名规则">
          <label>
            备份记录
            <select
              value={restoreBackupId}
              onChange={(event) => setRestoreBackupId(event.target.value)}
            >
              <option value="">请选择</option>
              {backups.map((backup) => (
                <option key={safe(backup.id)} value={safe(backup.id)}>
                  {when(backup.completedAt, true)} · {safe(backup.backupType)}
                </option>
              ))}
            </select>
          </label>
          <label>
            隔离目标
            <input
              value={restoreTarget}
              placeholder="travel_restore_drill_20260901_120000"
              onChange={(event) => setRestoreTarget(event.target.value)}
            />
          </label>
          <button
            className="primary"
            disabled={
              busy ||
              !restoreBackupId ||
              !/^travel_restore_drill_[0-9]{8}_[0-9]{6}$/u.test(restoreTarget)
            }
            onClick={() => void recordRestore()}
          >
            登记恢复验证
          </button>
        </Panel>
      </div>
      <p className="operations-rule-note">
        备份产物须有 SHA-256 校验，恢复演练必须使用独立目标。 运行指标：
        {Object.keys(runtime).length ? '已采集快照' : '等待运行采集'}；敏感日志自动脱敏。
      </p>
      <Panel title="备份与恢复证据" hint={`${recoveryCount} 条真实记录`}>
        <State state={recovery} count={recoveryCount} noun="备份或恢复演练记录">
          <div className="r5-table-scroll">
            <table className="r4-table">
              <thead>
                <tr>
                  <th>类型</th>
                  <th>状态</th>
                  <th>完整性/隔离目标</th>
                  <th>恢复目标</th>
                  <th>完成时间</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((row) => (
                  <tr key={safe(row.id)}>
                    <td>数据库备份</td>
                    <td>
                      <Pill value={row.status} />
                    </td>
                    <td>已登记 SHA-256 校验</td>
                    <td>{Number(row.rpoSeconds ?? 0)} 秒</td>
                    <td>{when(row.completedAt, true)}</td>
                  </tr>
                ))}
                {restores.map((row) => (
                  <tr key={safe(row.id)}>
                    <td>隔离恢复演练</td>
                    <td>
                      <Pill value={row.status} />
                    </td>
                    <td>已使用隔离恢复目标</td>
                    <td>{Number(row.rtoSeconds ?? 0)} 秒</td>
                    <td>{when(row.completedAt, true)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
    </>
  );
}

function SecurityReleaseContent({ api, targetAppId: appId, pageId, params }: PageProps) {
  const encoded = encodeURIComponent(appId);
  const empty = async () => ({ items: [] });
  const security = useTask(
    `r7-security-${appId}`,
    () =>
      pageId >= 'ADM-J01' && pageId <= 'ADM-J07'
        ? api.get(`/api/admin/m8/apps/${encoded}/security`)
        : empty(),
    `${pageId}:${appId}`,
  );
  const releases = useTask(
    `r7-releases-${appId}`,
    () =>
      ['ADM-J08', 'ADM-J09'].includes(pageId)
        ? api.get(`/api/admin/m8/apps/${encoded}/releases`)
        : empty(),
    `${pageId}:${appId}`,
  );
  const ops = useTask(
    `r7-ops-${appId}`,
    () => (pageId === 'ADM-J10' ? api.get(`/api/admin/m8/apps/${encoded}/ops/snapshot`) : empty()),
    `${pageId}:${appId}`,
  );
  const recovery = useTask(
    `r7-recovery-${appId}`,
    () => (pageId === 'ADM-J10' ? api.get(`/api/admin/m8/apps/${encoded}/recovery`) : empty()),
    `${pageId}:${appId}`,
  );
  const appsState = useTask(
    `r7-apps-${appId}`,
    () => (pageId === 'ADM-J04' ? api.get('/api/admin/m2/apps') : empty()),
    `${pageId}:${appId}`,
  );
  const citiesState = useTask(
    `r7-cities-${appId}`,
    () => (pageId === 'ADM-J04' ? api.get('/api/admin/m2/cities') : empty()),
    `${pageId}:${appId}`,
  );
  if (!appId)
    return (
      <div className="r4-state error">
        <b>请选择目标小程序</b>
        <p>安全、发布和运维数据必须在明确的小程序范围内访问。</p>
      </div>
    );
  if (pageId === 'ADM-J01') return <SecurityUsers api={api} appId={appId} state={security} />;
  if (pageId === 'ADM-J02') return <RoleManagement api={api} appId={appId} state={security} />;
  if (pageId === 'ADM-J03') return <PermissionMatrix api={api} appId={appId} state={security} />;
  if (pageId === 'ADM-J04')
    return (
      <DataScopes
        api={api}
        appId={appId}
        state={security}
        appsState={appsState}
        citiesState={citiesState}
      />
    );
  if (pageId === 'ADM-J05') return <FieldPolicies api={api} appId={appId} state={security} />;
  if (pageId === 'ADM-J06') return <HighRisk api={api} appId={appId} state={security} />;
  if (pageId === 'ADM-J07') return <SystemSettings state={security} />;
  if (pageId === 'ADM-J08') return <ReleaseCenter api={api} appId={appId} state={releases} />;
  if (pageId === 'ADM-J09')
    return <ReleaseGate api={api} appId={appId} state={releases} id={params.id ?? ''} />;
  return <Operations api={api} appId={appId} ops={ops} recovery={recovery} />;
}

export function CanonicalMessageFinancePage(props: PageProps) {
  return (
    <div className="operations-workspace">
      <MessageFinanceContent {...props} />
    </div>
  );
}

export function CanonicalSecurityReleasePage(props: PageProps) {
  return (
    <div className="operations-workspace">
      {['ADM-J01', 'ADM-J02', 'ADM-J03', 'ADM-J04', 'ADM-J05', 'ADM-J06', 'ADM-J11'].includes(
        props.pageId,
      ) ? (
        <BusinessSecurityWorkspace
          api={props.api}
          targetAppId={props.targetAppId}
          pageId={props.pageId}
          {...(props.params.staffMode === 'new' || props.params.staffMode === 'edit'
            ? { staffMode: props.params.staffMode }
            : {})}
          {...(props.params.userId ? { staffUserId: props.params.userId } : {})}
        />
      ) : (
        <SecurityReleaseContent {...props} />
      )}
    </div>
  );
}
